import React, { useEffect, useState, useRef } from "react";
import cta from "./Assets/cta-image.svg";

import { Drawer, Button, Grid, Carousel, List, Spin, Input, Modal, message, Tag, Popover } from "antd";

import {
  RightOutlined,
  CloseOutlined,
  LeftOutlined,
  DownOutlined,
  EnvironmentTwoTone,
  PhoneTwoTone,
  MailTwoTone,
  WhatsAppOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  UserOutlined,
  SearchOutlined,
  PlayCircleOutlined,
  FacebookOutlined,
  LinkedinOutlined,
  TwitterOutlined,
  YoutubeOutlined,
  StarFilled,
  BookOutlined,
  SafetyCertificateOutlined,
  VideoCameraOutlined,
  LockOutlined,
  UnlockOutlined,
} from "@ant-design/icons";

// Reveal
import AOS from "aos";
import "aos/dist/aos.css";

import "./styles.scss";
import "./button.scss";

import logo from "../../Assets/Logos/ibas_logo.jpeg";
import { getHomeCourses, getPublicCourseDetails, submitInquiry, getAllTestimonials, getAllAcademicPanelMembers } from "../../Constants/Api/functions/function";
import {
  DEFAULT_COURSE_COVER_IMAGE,
  DEFAULT_USER_PROFILE_IMAGE,
} from "../../Constants/Api/azure/azure";
import { Link } from "react-router-dom";

const PromoCountdown = ({ endDate }) => {
  const [timeLeft, setTimeLeft] = useState('');

  useEffect(() => {
    if (!endDate) return;
    
    const getTargetDate = () => {
      const target = new Date(endDate);
      const str = String(endDate);
      if (str.length <= 10 || (target.getUTCHours() === 0 && target.getUTCMinutes() === 0 && target.getUTCSeconds() === 0)) {
        target.setHours(23, 59, 59, 999);
      }
      return target;
    };

    const calculateTimeLeft = () => {
      const difference = +getTargetDate() - +new Date();
      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
        const minutes = Math.floor((difference / 1000 / 60) % 60);
        const seconds = Math.floor((difference / 1000) % 60);

        if (days > 0) {
          return `Ends in ${days}d ${hours}h ${minutes}m`;
        }
        return `Ends in ${hours}h ${minutes}m ${seconds}s`;
      }
      return null;
    };

    setTimeLeft(calculateTimeLeft());
    const timer = setInterval(() => {
      const remaining = calculateTimeLeft();
      setTimeLeft(remaining);
      if (!remaining) clearInterval(timer);
    }, 1000);

    return () => clearInterval(timer);
  }, [endDate]);

  if (!timeLeft) return null;

  return (
    <div style={{
      display: 'inline-flex',
      alignItems: 'center',
      padding: '2px 8px',
      background: '#fff2f0',
      border: '1px solid #ffccc7',
      borderRadius: '4px',
      color: '#ff4d4f',
      fontSize: '11px',
      fontWeight: 'bold',
      marginTop: '4px',
      whiteSpace: 'nowrap'
    }}>
      <ClockCircleOutlined style={{ marginRight: '4px' }} />
      {timeLeft}
    </div>
  );
};

// Standard currency formatter with comma separation, e.g. "10,000 LKR"
const formatLKR = (amount) => {
  if (amount === undefined || amount === null || amount === "") return "";
  const num = Number(amount);
  if (isNaN(num)) return `${amount} LKR`;
  return `${Math.round(num).toLocaleString("en-US")} LKR`;
};

// Course time period / duration helper
const getCourseDuration = (item) => {
  if (item?.duration && typeof item.duration === "string" && item.duration.trim()) {
    return item.duration.trim();
  }
  if (item?.description && typeof item.description === "string") {
    const match = item.description.match(/(?:Duration|⏳\s*Duration)\s*:\s*([^<\n\r]+)/i);
    if (match && match[1]) {
      const clean = match[1].replace(/<\/?[^>]+(>|$)/g, "").trim();
      if (clean) return clean;
    }
    const periodMatch = item.description.match(/(\d+(?:\.\d+)?\s*(?:Months?|Weeks?|Days?|Hours?))/i);
    if (periodMatch && periodMatch[1]) {
      return periodMatch[1].trim();
    }
  }
  return "Flexible Duration";
};

// Check if course is currently within an active promotional period
const isCourseInPromoPeriod = (course) => {
  const numPrice = Number(course?.price);
  const numPromo = Number(course?.promoPrice);

  if (isNaN(numPromo) || numPromo <= 0 || isNaN(numPrice) || numPromo >= numPrice) {
    return false;
  }

  // Promotional pricing must be active
  const isPromoFlagActive = course?.isPromoActive === 1 || course?.isPromoActive === true || course?.isPromoActive === "1";
  if (!isPromoFlagActive) {
    return false;
  }

  // Must have a promo end date that has not elapsed
  if (!course?.promoEndDate) {
    return false;
  }

  const now = new Date();
  const end = new Date(course.promoEndDate);
  const str = String(course.promoEndDate);
  if (str.length <= 10 || (end.getUTCHours() === 0 && end.getUTCMinutes() === 0 && end.getUTCSeconds() === 0)) {
    end.setHours(23, 59, 59, 999);
  }

  if (now > end) {
    return false;
  }

  if (course?.promoStartDate) {
    const start = new Date(course.promoStartDate);
    if (now < start) {
      return false;
    }
  }

  return true;
};

// Standard specifications for Why Choose IBAS (matching all 8 requirements)
const specifications = [
  {
    title: "Government Registered Institute",
    description: "Government Registered Higher Education Institute under PV No: 00264413, ensuring certified legitimacy.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    title: "Recognized Programs",
    description: "Accredited and recognized by the Association of Accounting Technicians of Sri Lanka (AAT) and other key institutions.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    title: "Industry Expert Lecturers",
    description: "Learn from Senior Chartered Accountants, University Professors, Professional Managers, Legal Experts, and Academic Administrators.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="9" cy="7" r="4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    title: "Flexible Learning Options",
    description: "Offering the best of hybrid learning, allowing you to choose between physical classroom learning and fully online classes.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <rect x="2" y="3" width="20" height="14" rx="2" ry="2" strokeWidth="2" />
        <line x1="8" y1="21" x2="16" y2="21" strokeWidth="2" />
        <line x1="12" y1="17" x2="12" y2="21" strokeWidth="2" />
      </svg>
    ),
  },
  {
    title: "Online LMS Access",
    description: "Get lifetime access to study materials, class recordings, live webinar feeds, and quizzes through our modern LMS.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    title: "Career Focused Education",
    description: "Our practical skill curricula are tailored strictly around employment standards and professional market demands.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <rect x="2" y="7" width="20" height="14" rx="2" ry="2" strokeWidth="2" />
        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" strokeWidth="2" />
      </svg>
    ),
  },
  {
    title: "Practical Skill Development",
    description: "Direct hands-on experience using computer systems, industry tools, dashboards, and live-project templates.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" strokeWidth="2" />
      </svg>
    ),
  },
  {
    title: "Affordable Fees",
    description: "Get international quality corporate learning, flexible installment payments, and affordable course structures.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <rect x="2" y="5" width="20" height="14" rx="2" strokeWidth="2" />
        <line x1="2" y1="10" x2="22" y2="10" strokeWidth="2" />
      </svg>
    ),
  },
];

const statsData = [
  {
    label: "Active Students",
    value: "2,500+",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="9" cy="7" r="4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M22 21v-2a4 4 0 0 0-3-3.87" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    label: "Industry Certifications",
    value: "15+",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    label: "Professional Courses",
    value: "50+",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path d="M12 7v14" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    label: "Student Satisfaction",
    value: "98%",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
];

const features = [
  "Government Registered higher education standards",
  "Senior Chartered Accountants & Expert Faculty",
  "Lifetime LMS access with recorded webinars",
  "Direct WhatsApp support and career alignment",
];

const testimonials = [
  {
    id: 1,
    content: "The QuickBooks and taxation diploma program at IBAS was outstanding. The lecturer, a Senior Chartered Accountant, gave us actual company files to audit. Within a month after graduating, I secured a Senior Accountant job in Gampaha!",
    author: "Dinuka Hasaranga",
    course: "Diploma in Accounting & QuickBooks",
    rating: 5,
    image: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80",
  },
  {
    id: 2,
    content: "IBAS completely redefined flexible online learning. Since I am employed full-time in Colombo, attending physical classes was impossible. Their LMS portal let me access recorded webinars and download Excel dashboards late at night. Extremely convenient!",
    author: "Shalani Wijewardena",
    course: "Advanced Excel & Data Analytics",
    rating: 5,
    image: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80",
  },
  {
    id: 3,
    content: "Our AAT examination panel at IBAS was excellent. They literally mapped out exactly how to structure calculations and answers. The support was top-tier, answering questions via WhatsApp at any hour. Proudly qualified AAT on my first attempt!",
    author: "Mineth Rajapaksha",
    course: "AAT Professional Exam Classes",
    rating: 5,
    image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80",
  },
  {
    id: 4,
    content: "The ChatGPT and Prompt Engineering course was a massive game-changer. I learned how to automate marketing copy and manage client projects in half the time. Truly cutting-edge curriculum that is rarely found in other Sri Lankan institutes.",
    author: "Dilani Alwis",
    course: "AI for Business & ChatGPT",
    rating: 5,
    image: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80",
  },
];

// Exact Course categories matching 100% of specifications
const courseCategories = [
  {
    id: "management",
    name: "Management",
    icon: (
      <svg className="category-icon-svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
    description: "Develop operational excellence, robust business management workflows, human resource practices, and startup launch metrics.",
    subCourses: [
      "Business Management",
      "Human Resource Management",
      "Entrepreneurship",
      "Project Management",
      "Office Administration"
    ]
  },
  {
    id: "accounting",
    name: "Accounting & Finance",
    icon: (
      <svg className="category-icon-svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    description: "Master financial accounting standards, tax codes, audit compliance, QuickBooks software, and cash flow governance.",
    subCourses: [
      "Diploma in Accounting",
      "QuickBooks Accounting",
      "Computerized Accounting",
      "Taxation",
      "Auditing",
      "Financial Management"
    ]
  },
  {
    id: "data-it",
    name: "Data Analytics & IT",
    icon: (
      <svg className="category-icon-svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 002 2h2a2 2 0 002-2z" />
      </svg>
    ),
    description: "Unlock business intelligence, compile data models in Power BI, design custom dashboard reports, and execute advanced Excel algorithms.",
    subCourses: [
      "Advanced Excel",
      "Power BI",
      "Data Analytics",
      "Dashboard Reporting",
      "Business Intelligence"
    ]
  },
  {
    id: "ai",
    name: "Artificial Intelligence (AI)",
    icon: (
      <svg className="category-icon-svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
    ),
    description: "Accelerate productivity with modern AI models. Develop expert prompt engineering skills and automate tasks using ChatGPT.",
    subCourses: [
      "AI for Business",
      "ChatGPT & AI Tools",
      "Prompt Engineering",
      "AI Productivity",
      "AI for Marketing"
    ]
  },
  {
    id: "logistics",
    name: "Logistics & Supply Chain",
    icon: (
      <svg className="category-icon-svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
      </svg>
    ),
    description: "Govern global warehouse operations, container shipping routing, vendor procurement matrices, and supply chains.",
    subCourses: [
      "Logistics Management",
      "Warehouse Management",
      "Shipping Management",
      "Procurement Management"
    ]
  },
  {
    id: "design",
    name: "Graphic Design & Multimedia",
    icon: (
      <svg className="category-icon-svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
    description: "Acquire mastery over Illustrator path alignments, Photoshop compositing, Canva dashboard features, and high-impact video editing.",
    subCourses: [
      "Graphic Design",
      "Photoshop",
      "Illustrator",
      "Canva Design",
      "Video Editing"
    ]
  },
  {
    id: "architecture",
    name: "Architecture & Design",
    icon: (
      <svg className="category-icon-svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
    ),
    description: "Draft high-fidelity architectural models, detailed AutoCAD orthographics, interior visualization renders, and 3D modeling.",
    subCourses: [
      "AutoCAD",
      "Architectural Design",
      "Interior Design",
      "3D Visualization"
    ]
  },
  {
    id: "professional-exams",
    name: "Professional Exam Classes",
    icon: (
      <svg className="category-icon-svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" />
      </svg>
    ),
    description: "Accelerate your success in core certified exams. Comprehensive support for AAT Sri Lanka, Chartered Accounting, and SLIM classes.",
    subCourses: [
      "Association of Accounting Technicians of Sri Lanka (AAT)",
      "Chartered Accounting",
      "Sri Lanka Institute of Marketing (SLIM)",
      "Professional Business Qualifications"
    ]
  }
];

// Dedicated accredited professional examination programs for IBAS
const PROFESSIONAL_EXAM_PROGRAMS = [
  {
    id: "pro-exam-aat",
    examType: "aat",
    name: "AAT Sri Lanka - Level I, II & III Comprehensive Examination Coaching",
    description: "Official preparatory curriculum for the Association of Accounting Technicians of Sri Lanka (AAT). Covers Level I (Foundation), Level II (Intermediate), and Level III (Final). Includes complete past paper discussions, computer-based accounting practice, and structured model test series.",
    coverImage: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=600&h=400&fit=crop",
    price: "Installment Plans Available",
    duration: "4 - 6 Months / Level",
    lecturer: "Senior Chartered Accountants & AAT Fellows",
    tags: "AAT Sri Lanka, Accounting, Professional Exams",
    whatsappGroupLink: "https://wa.me/94712163163?text=I%20am%20inquiring%20about%20AAT%20Sri%20Lanka%20Exam%20Classes",
    orderNumber: 1,
    status: "Enrollment Open",
    syllabus: [
      { id: 'aat-1', title: 'Level I: Financial Accounting & Business Calculations', isFree: 1 },
      { id: 'aat-2', title: 'Level II: Advanced Financial Accounting & Costing', isFree: 0 },
      { id: 'aat-3', title: 'Level III: Financial Reporting, Audit & Assurance', isFree: 0 },
    ]
  },
  {
    id: "pro-exam-ca",
    examType: "ca",
    name: "CA Sri Lanka - Business & Corporate Level Preparatory Coaching",
    description: "Comprehensive coaching for the Institute of Chartered Accountants of Sri Lanka (CA Sri Lanka) examinations. Delivered by seasoned FCA/ACA members focusing on Financial Accounting, Taxation, Governance, and Corporate Reporting.",
    coverImage: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=600&h=400&fit=crop",
    price: "Modular Fee Structure",
    duration: "6 Months / Level",
    lecturer: "Senior FCA / ACA Faculty Members",
    tags: "CA Sri Lanka, Chartered Accounting, Professional Exams",
    whatsappGroupLink: "https://wa.me/94712163163?text=I%20am%20inquiring%20about%20CA%20Sri%20Lanka%20Classes",
    orderNumber: 2,
    status: "Enrollment Open",
    syllabus: [
      { id: 'ca-1', title: 'Business Level I: Accounting & Financial Environment', isFree: 1 },
      { id: 'ca-2', title: 'Business Level II: Business Law & Management Accounting', isFree: 0 },
      { id: 'ca-3', title: 'Corporate Level: Financial Strategy & Integrated Case Study', isFree: 0 },
    ]
  },
  {
    id: "pro-exam-cma",
    examType: "cma",
    name: "CMA Sri Lanka - Professional Examination Coaching (Stages 1 - 4)",
    description: "Certified Management Accountants (CMA Sri Lanka) preparatory program. Master Management Accounting, Financial Accounting, Strategic Management, and Business Taxation with expert industry practitioners.",
    coverImage: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&h=400&fit=crop",
    price: "Installment Plans Available",
    duration: "5 - 6 Months",
    lecturer: "Senior FCMA Faculty",
    tags: "CMA Sri Lanka, Management Accounting, Professional Exams",
    whatsappGroupLink: "https://wa.me/94712163163?text=I%20am%20inquiring%20about%20CMA%20Sri%20Lanka%20Classes",
    orderNumber: 3,
    status: "Enrollment Open",
    syllabus: [
      { id: 'cma-1', title: 'Foundation & Operational: Management Accounting Fundamentals', isFree: 1 },
      { id: 'cma-2', title: 'Managerial Level: Advanced Management Accounting & Financial Reporting', isFree: 0 },
      { id: 'cma-3', title: 'Strategic Level: Strategic Business Management & Audit', isFree: 0 },
    ]
  },
  {
    id: "pro-exam-slim",
    examType: "slim",
    name: "SLIM Qualifications - Professional Diploma in Marketing (PCM & PDM)",
    description: "Sri Lanka Institute of Marketing (SLIM) preparatory classes. Covers Preliminary Certificate in Marketing (PCM) and Professional Diploma in Marketing (PDM) for aspiring marketing executives and brand directors.",
    coverImage: "https://images.unsplash.com/photo-1557804506-669a67965ba0?w=600&h=400&fit=crop",
    price: "Flexible Payment Options",
    duration: "4 Months",
    lecturer: "Chartered Marketers & Senior Industry Executives",
    tags: "SLIM, Marketing, Professional Exams",
    whatsappGroupLink: "https://wa.me/94712163163?text=I%20am%20inquiring%20about%20SLIM%20Marketing%20Classes",
    orderNumber: 4,
    status: "Enrollment Open",
    syllabus: [
      { id: 'slim-1', title: 'PCM: Marketing Fundamentals & Consumer Dynamics', isFree: 1 },
      { id: 'slim-2', title: 'PDM Module 1: Brand Planning & Digital Strategies', isFree: 0 },
      { id: 'slim-3', title: 'PDM Module 2: Strategic Sales & Integrated Campaigns', isFree: 0 },
    ]
  }
];

const academicPanel = [
  {
    title: "Senior Chartered Accountants",
    desc: "Overseeing all accounting, tax, auditing, and corporate governance curricula to ensure direct industry alignment.",
    icon: "💼"
  },
  {
    title: "University Professors",
    desc: "Structuring academic methodologies, analytical concepts, research benchmarks, and business theory models.",
    icon: "🎓"
  },
  {
    title: "Professional Managers",
    desc: "Delivering real-world management case studies, leadership patterns, human resource practices, and logistics workflows.",
    icon: "🤝"
  },
  {
    title: "Legal Experts",
    desc: "Covering taxation codes, business formation laws, statutory regulations, and Sri Lankan employment legislature.",
    icon: "⚖️"
  },
  {
    title: "Industry Specialists",
    desc: "Leading practical workshops on advanced Excel models, Power BI reporting, and custom dashboard development.",
    icon: "🚀"
  },
  {
    title: "Academic Administrators",
    desc: "Managing student enrollment schedules, webinar integrations, remote exam boards, and LMS software stability.",
    icon: "🖥️"
  }
];

const Landing = () => {
  const carouselRef = useRef(null);
  const [courses, setCourses] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);

  // Search state and category filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("management");
  const [expandedCategory, setExpandedCategory] = useState(null); // mobile accordion
  const [videoModalVisible, setVideoModalVisible] = useState(false);

  // Course Finder & Filter States (Udemy Style & Professional Examinations)
  const [finderMode, setFinderMode] = useState("category"); // 'category' | 'professional-exams'
  const [selectedFinderCategory, setSelectedFinderCategory] = useState("all");
  const [profExamFilter, setProfExamFilter] = useState("all"); // 'all' | 'aat' | 'ca' | 'cma' | 'slim'
  const [priceFilter, setPriceFilter] = useState("all"); // 'all' | 'free' | 'promo' | 'standard'

  // Navbar Courses Mega Popover Step State
  const [navCoursesOpen, setNavCoursesOpen] = useState(false);
  const [hoveredNavCategory, setHoveredNavCategory] = useState("accounting");
  const [mobileNavCoursesExpanded, setMobileNavCoursesExpanded] = useState(false);
  const [mobileNavSelectedCategory, setMobileNavSelectedCategory] = useState("accounting");

  // Card description expand/collapse state
  const [expandedDescMap, setExpandedDescMap] = useState({});
  const toggleDesc = (id) => {
    setExpandedDescMap((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // Public Course Preview & Free Video Modal State
  const [coursePreviewModalVisible, setCoursePreviewModalVisible] = useState(false);
  const [selectedCourseForPreview, setSelectedCourseForPreview] = useState(null);
  const [coursePreviewLoading, setCoursePreviewLoading] = useState(false);
  const [coursePreviewContents, setCoursePreviewContents] = useState([]);
  const [activePreviewVideo, setActivePreviewVideo] = useState("");

  const getEmbedVideoUrl = (url) => {
    if (!url) return "";
    const ytMatch = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=|shorts\/))([\w-]{11})/);
    if (ytMatch && ytMatch[1]) {
      return `https://www.youtube.com/embed/${ytMatch[1]}?autoplay=1&rel=0`;
    }
    const vimeoMatch = url.match(/vimeo\.com\/(?:video\/)?([0-9]+)/);
    if (vimeoMatch && vimeoMatch[1]) {
      return `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1`;
    }
    if (url.includes("drive.google.com")) {
      return url.replace(/\/view(\?usp=sharing)?$/, "/preview");
    }
    return url;
  };

  const stripHtml = (html) => {
    if (!html) return "";
    try {
      const tmp = document.createElement("DIV");
      tmp.innerHTML = html;
      return tmp.textContent || tmp.innerText || "";
    } catch {
      return html.replace(/<[^>]*>?/gm, '');
    }
  };

  const handleOpenCoursePreview = async (course) => {
    setSelectedCourseForPreview(course);
    setActivePreviewVideo(course?.videoUrl || "");
    setCoursePreviewContents(course?.syllabus || []);
    setCoursePreviewModalVisible(true);
    setCoursePreviewLoading(true);
    try {
      if (course?.id && !String(course.id).startsWith("pro-exam-")) {
        const details = await getPublicCourseDetails(course.id);
        if (details && details.success) {
          setSelectedCourseForPreview(details.course);
          setCoursePreviewContents(details.contents || []);
          if (!course?.videoUrl && details.contents && details.contents.length > 0) {
            const firstFree = details.contents.find((c) => c.isFree && c.videoUrl);
            if (firstFree) {
              setActivePreviewVideo(firstFree.videoUrl);
            }
          }
        }
      }
    } catch (err) {
      console.error("Failed to load public course details:", err);
    } finally {
      setCoursePreviewLoading(false);
    }
  };

  // Inquiry Form state
  const [inquiryName, setInquiryName] = useState("");
  const [inquiryEmail, setInquiryEmail] = useState("");
  const [inquiryPhone, setInquiryPhone] = useState("");
  const [inquiryCourse, setInquiryCourse] = useState("");
  const [inquiryMessage, setInquiryMessage] = useState("");
  const [selectedCourseId, setSelectedCourseId] = useState(null);

  // Dynamic website content & submissions state
  const [dynamicTestimonials, setDynamicTestimonials] = useState([]);
  const [dynamicAcademicPanel, setDynamicAcademicPanel] = useState([]);
  const [isInquirySubmitting, setIsInquirySubmitting] = useState(false);

  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const isMobile = !screens.lg;

  const showDrawer = () => setDrawerVisible(true);
  const closeDrawer = () => setDrawerVisible(false);

  useEffect(() => {
    AOS.init({
      disable: false,
      startEvent: "DOMContentLoaded",
      initClassName: "aos-init",
      animatedClassName: "aos-animate",
      useClassNames: false,
      disableMutationObserver: false,
      debounceDelay: 50,
      throttleDelay: 99,
      offset: 120,
      delay: 0,
      duration: 500,
      easing: "ease",
      once: false,
      mirror: false,
      anchorPlacement: "top-bottom",
    });

    const timer = setTimeout(() => {
      AOS.refresh();
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (!el) return;

    const header = document.querySelector("header");
    const headerOffset = header ? header.clientHeight + 16 : 0;
    const elementY = el.getBoundingClientRect().top + window.pageYOffset;

    window.scrollTo({
      top: elementY - headerOffset,
      behavior: "smooth",
    });

    if (isMobile) closeDrawer();
  };

  useEffect(() => {
    handleGetHomeCourses();
    handleGetWebsiteContent();
  }, []);

  useEffect(() => {
    if (window.location.hash) {
      const sectionId = window.location.hash.substring(1);
      setTimeout(() => {
        scrollToSection(sectionId);
      }, 500);
    }
  }, []);

  const handleGetWebsiteContent = async () => {
    try {
      const tests = await getAllTestimonials();
      if (tests && tests.length > 0) {
        setDynamicTestimonials(tests);
      }
      const panel = await getAllAcademicPanelMembers();
      if (panel && panel.length > 0) {
        setDynamicAcademicPanel(panel);
      }
    } catch (err) {
      console.error("Error fetching dynamic website content:", err);
    }
  };

  const handleGetHomeCourses = async () => {
    setIsLoading(true);
    try {
      const response = await getHomeCourses();
      if (response && response.length > 0) {
        // Defensive deduplication to guarantee each course appears strictly once
        const seenIds = new Set();
        const uniqueCourses = response.filter((item) => {
          if (!item || !item.id) return false;
          if (seenIds.has(item.id)) return false;
          seenIds.add(item.id);
          return true;
        });

        const sortedResponse = uniqueCourses.sort(
          (a, b) => a?.orderNumber - b?.orderNumber
        );
        setCourses(sortedResponse);
      } else {
        setCourses([]);
      }
    } catch (error) {
      console.error(error);
      setCourses([]); // robust fallback
    }
    setIsLoading(false);
  };

  // Run Course Search & Filter
  const handleSearchSubmit = (e) => {
    if (e) e.preventDefault();
    scrollToSection("courses-section");
  };

  const handleTagClick = (tag) => {
    setSearchQuery(tag);
    scrollToSection("courses-section");
  };

  const handleResetAllFilters = () => {
    setSearchQuery("");
    setSelectedFinderCategory("all");
    setPriceFilter("all");
    setFinderMode("category");
    setProfExamFilter("all");
  };

  const handleSubcourseClick = (subName, categoryId) => {
    const subQueryMap = {
      "Business Management": "Business",
      "Human Resource Management": "Management",
      "Entrepreneurship": "Business",
      "Project Management": "Management",
      "Office Administration": "Office",
      "Diploma in Accounting": "Accounting",
      "QuickBooks Accounting": "QuickBooks",
      "Computerized Accounting": "Accounting",
      "Taxation": "Accounting",
      "Auditing": "Accounting",
      "Financial Management": "Accounting",
      "Advanced Excel": "Excel",
      "Power BI": "Power BI",
      "Data Analytics": "Analytics",
      "Dashboard Reporting": "Power BI",
      "Business Intelligence": "Analytics",
      "AI for Business": "AI",
      "ChatGPT & AI Tools": "AI",
      "Prompt Engineering": "AI",
      "AI Productivity": "AI",
      "AI for Marketing": "AI",
      "Logistics Management": "Logistics",
      "Warehouse Management": "Warehouse",
      "Shipping Management": "Export",
      "Procurement Management": "Supply",
      "Graphic Design": "Design",
      "Photoshop": "Design",
      "Illustrator": "Design",
      "Canva Design": "Design",
      "Video Editing": "Design",
      "AutoCAD": "Design",
      "Architectural Design": "Design",
      "Association of Accounting Technicians of Sri Lanka (AAT)": "aat",
      "Chartered Accounting": "ca",
      "Sri Lanka Institute of Marketing (SLIM)": "slim",
    };

    if (categoryId === "professional-exams") {
      setFinderMode("professional-exams");
      const mapped = subQueryMap[subName] || "all";
      setProfExamFilter(mapped);
      setSearchQuery("");
    } else {
      setFinderMode("category");
      setSelectedFinderCategory(categoryId);
      const query = subQueryMap[subName] || subName;
      setSearchQuery(query);
    }
    scrollToSection("courses-section");
  };

  // Navbar Step-by-Step Courses Helper
  const getNavbarCoursesByCategory = (catId) => {
    if (catId === "professional-exams") {
      let proList = [...PROFESSIONAL_EXAM_PROGRAMS];
      if (courses && courses.length > 0) {
        const matchingDbCourses = courses.filter((c) => {
          const name = (c.name || "").toLowerCase();
          const tags = (c.tags || "").toLowerCase();
          return (
            name.includes("aat") ||
            name.includes("cma") ||
            name.includes("ca ") ||
            name.includes("chartered") ||
            name.includes("slim") ||
            tags.includes("exam") ||
            tags.includes("professional")
          );
        });
        proList = [...proList, ...matchingDbCourses];
      }
      return proList;
    }

    if (!courses) return [];

    return courses.filter((c) => {
      const courseName = (c.name || "").toLowerCase();
      const courseTags = (c.tags || "").toLowerCase();

      if (catId === "management") {
        return (
          courseName.includes("management") ||
          courseName.includes("wherehous") ||
          courseName.includes("warehouse") ||
          courseName.includes("business") ||
          courseName.includes("logistics") ||
          courseTags.includes("management") ||
          courseTags.includes("warehouse")
        );
      } else if (catId === "accounting") {
        return (
          courseName.includes("accounting") ||
          courseName.includes("quickbooks") ||
          courseName.includes("quick books") ||
          courseName.includes("xero") ||
          courseName.includes("myob") ||
          courseName.includes("tax") ||
          courseName.includes("finance") ||
          courseTags.includes("accounting") ||
          courseTags.includes("quickbooks")
        );
      } else if (catId === "data-it") {
        return (
          courseName.includes("excel") ||
          courseName.includes("power bi") ||
          courseName.includes("powerbi") ||
          courseName.includes("analytics") ||
          courseName.includes("data") ||
          courseTags.includes("data") ||
          courseTags.includes("power bi") ||
          courseTags.includes("excel")
        );
      } else if (catId === "ai") {
        return (
          courseName.includes("ai") ||
          courseName.includes("intelligence") ||
          courseName.includes("chatgpt") ||
          courseName.includes("prompt") ||
          courseTags.includes("ai")
        );
      } else if (catId === "logistics") {
        return (
          courseName.includes("logistics") ||
          courseName.includes("supply") ||
          courseName.includes("shipping") ||
          courseName.includes("wherehous") ||
          courseName.includes("warehouse") ||
          courseName.includes("import") ||
          courseName.includes("export") ||
          courseTags.includes("logistics") ||
          courseTags.includes("warehouse") ||
          courseTags.includes("supply")
        );
      } else if (catId === "design") {
        return (
          courseName.includes("design") ||
          courseName.includes("web") ||
          courseName.includes("photoshop") ||
          courseName.includes("illustrator") ||
          courseName.includes("video") ||
          courseTags.includes("design") ||
          courseTags.includes("trainingandcourses")
        );
      } else if (catId === "architecture") {
        return (
          courseName.includes("autocad") ||
          courseName.includes("architecture") ||
          courseName.includes("drafting") ||
          courseTags.includes("architecture")
        );
      }
      return true;
    });
  };

  const handleSelectCourseFromNavbar = (course, categoryId) => {
    setNavCoursesOpen(false);
    if (isMobile) closeDrawer();

    let targetCat = categoryId;
    if (!targetCat) {
      if (course.examType || (course.id && String(course.id).startsWith("pro-exam-"))) {
        targetCat = "professional-exams";
      } else {
        for (const cat of courseCategories) {
          const catCourses = getNavbarCoursesByCategory(cat.id);
          if (catCourses.some((c) => c.id === course.id || c.name === course.name)) {
            targetCat = cat.id;
            break;
          }
        }
      }
    }
    targetCat = targetCat || hoveredNavCategory || mobileNavSelectedCategory || "all";

    if (targetCat === "professional-exams" || course.examType || (course.id && String(course.id).startsWith("pro-exam-"))) {
      setFinderMode("professional-exams");
      if (course.examType) {
        setProfExamFilter(course.examType);
      } else {
        setProfExamFilter("all");
      }
    } else {
      setFinderMode("category");
      setSelectedFinderCategory(targetCat);
    }

    if (course.id) {
      setSelectedCourseId(course.id);
    }
    if (course.name) {
      setInquiryCourse(course.name);
    }

    // Smooth scroll directly to the selected course card or courses section
    setTimeout(() => {
      const cardEl = document.getElementById(`course-card-${course.id}`);
      if (cardEl) {
        cardEl.scrollIntoView({ behavior: "smooth", block: "center" });
      } else {
        scrollToSection("courses-section");
      }
    }, 250);

    handleOpenCoursePreview(course);

    message.success({
      content: `Selected "${course.name}"!`,
      duration: 3,
    });
  };

  const handleSelectCategoryFromNavbar = (categoryId) => {
    setNavCoursesOpen(false);
    if (isMobile) closeDrawer();

    if (categoryId === "professional-exams") {
      setFinderMode("professional-exams");
      setProfExamFilter("all");
      setSearchQuery("");
    } else {
      setFinderMode("category");
      setSelectedFinderCategory(categoryId);
      setSearchQuery("");
    }
    scrollToSection("courses-section");
  };

  const renderNavbarCourseSteps = () => {
    const selectedCategoryObj =
      courseCategories.find((c) => c.id === hoveredNavCategory) || courseCategories[0];
    const categoryCourses = getNavbarCoursesByCategory(hoveredNavCategory);

    return (
      <div className="navbar-course-steps-container">
        {/* Step Guide Bar Header */}
        <div className="steps-guide-header">
          <div className="step-guide-item">
            <span className="step-num-badge">1</span>
            <div className="step-info">
              <span className="step-title">Step 1: Category</span>
              <span className="step-desc">Browse study domains</span>
            </div>
          </div>
          <span className="step-divider-arrow">→</span>
          <div className="step-guide-item">
            <span className="step-num-badge">2</span>
            <div className="step-info">
              <span className="step-title">Step 2: Choose Course</span>
              <span className="step-desc">Inspect available classes</span>
            </div>
          </div>
          <span className="step-divider-arrow">→</span>
          <div className="step-guide-item">
            <span className="step-num-badge">3</span>
            <div className="step-info">
              <span className="step-title">Step 3: Select & Learn</span>
              <span className="step-desc">View preview & syllabus</span>
            </div>
          </div>
        </div>

        {/* 2-Column Body: Step 1 (Categories) & Step 2 (Courses) */}
        <div className="steps-columns-body">
          {/* Step 1: Category Column */}
          <div className="steps-categories-col">
            <div className="col-header-label">1. Choose Category</div>
            <div className="categories-nav-list">
              {courseCategories.map((cat) => {
                const count = getNavbarCoursesByCategory(cat.id).length;
                const isSelected = hoveredNavCategory === cat.id;
                return (
                  <div
                    key={cat.id}
                    className={`nav-category-row ${isSelected ? "selected" : ""}`}
                    onMouseEnter={() => setHoveredNavCategory(cat.id)}
                    onClick={() => setHoveredNavCategory(cat.id)}
                  >
                    <div className="cat-row-left">
                      <span className="cat-icon-wrapper">{cat.icon}</span>
                      <span className="cat-name-label">{cat.name}</span>
                    </div>
                    <div className="cat-row-right">
                      <span className="cat-count-badge">{count}</span>
                      <RightOutlined className="cat-arrow-icon" />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Step 2 & 3: Courses Column */}
          <div className="steps-courses-col">
            <div className="col-header-label flex-between">
              <span>
                2. SELECT COURSE IN {selectedCategoryObj?.name?.toUpperCase()} ({categoryCourses.length})
              </span>
              <span
                className="view-all-link"
                onClick={() => handleSelectCategoryFromNavbar(selectedCategoryObj.id)}
              >
                View Section →
              </span>
            </div>

            <div className="courses-nav-list">
              {categoryCourses.length > 0 ? (
                categoryCourses.map((c) => (
                  <div
                    key={c.id || c.name}
                    className={`nav-course-item-card ${selectedCourseId === c.id ? "is-selected-card" : ""}`}
                    onClick={() => handleSelectCourseFromNavbar(c, selectedCategoryObj.id)}
                  >
                    <div className="course-thumb-box">
                      <img
                        src={
                          c.coverImage ||
                          c.image ||
                          "https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=300&h=200&fit=crop"
                        }
                        alt={c.name}
                      />
                    </div>
                    <div className="course-meta-box">
                      <div className="course-title-line" title={c.name}>
                        {c.name}
                      </div>
                      <div className="course-sub-line">
                        <span className="duration-tag">
                          <ClockCircleOutlined /> {c.duration || "Flexible"}
                        </span>
                        {c.lecturer && (
                          <span className="lecturer-tag" title={c.lecturer}>
                            • {c.lecturer}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="course-action-box">
                      <Tag color={c.price === "Free" ? "green" : "gold"}>
                        {c.price || "Rs. 25,000"}
                      </Tag>
                      <span className={`select-action-text ${selectedCourseId === c.id ? "active-selected" : ""}`}>
                        {selectedCourseId === c.id ? "Selected ✓" : "Select Course →"}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-nav-courses-msg">
                  No courses currently listed for this category.
                </div>
              )}
            </div>

            <div className="courses-col-footer">
              <Button
                block
                className="btn-select-category-courses"
                onClick={() => handleSelectCategoryFromNavbar(selectedCategoryObj.id)}
              >
                View All {selectedCategoryObj.name} Courses ({categoryCourses.length})
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Filter courses list dynamically
  const getFilteredCourses = () => {
    // 1. Professional Exams Mode
    if (finderMode === "professional-exams") {
      let proList = [...PROFESSIONAL_EXAM_PROGRAMS];

      if (profExamFilter === "aat") {
        proList = proList.filter((c) => c.examType === "aat");
      } else if (profExamFilter === "ca") {
        proList = proList.filter((c) => c.examType === "ca");
      } else if (profExamFilter === "cma") {
        proList = proList.filter((c) => c.examType === "cma");
      } else if (profExamFilter === "slim") {
        proList = proList.filter((c) => c.examType === "slim");
      }

      // Also append any matching database courses
      if (courses && courses.length > 0) {
        const matchingDbCourses = courses.filter((c) => {
          const name = (c.name || "").toLowerCase();
          const tags = (c.tags || "").toLowerCase();
          if (profExamFilter === "aat") {
            return name.includes("aat") || tags.includes("aat");
          } else if (profExamFilter === "ca") {
            return name.includes("ca ") || name.includes("chartered") || tags.includes("chartered");
          } else if (profExamFilter === "cma") {
            return name.includes("cma") || tags.includes("cma");
          } else if (profExamFilter === "slim") {
            return name.includes("slim") || tags.includes("slim");
          }
          return (
            name.includes("aat") ||
            name.includes("cma") ||
            name.includes("ca ") ||
            name.includes("chartered") ||
            name.includes("slim") ||
            tags.includes("exam") ||
            tags.includes("professional")
          );
        });
        proList = [...proList, ...matchingDbCourses];
      }

      // Query filter within professional exams
      if (searchQuery.trim() !== "") {
        const q = searchQuery.toLowerCase().trim();
        proList = proList.filter((c) => {
          const name = (c.name || "").toLowerCase();
          const lecturer = (c.lecturer || "").toLowerCase();
          const tags = (c.tags || "").toLowerCase();
          const desc = c.description ? stripHtml(c.description).toLowerCase() : "";
          return name.includes(q) || lecturer.includes(q) || tags.includes(q) || desc.includes(q);
        });
      }

      return proList;
    }

    // 2. Standard Category Mode
    if (!courses) return [];

    return courses.filter((c) => {
      const courseName = (c.name || "").toLowerCase();
      const lecturerName = c.lecturer ? c.lecturer.toLowerCase() : "";
      const courseTags = c.tags ? c.tags.toLowerCase() : "";
      const courseDesc = c.description ? stripHtml(c.description).toLowerCase() : "";
      const priceNum = Number(c.price);
      const isFree = c.price === 0 || c.price === "Free" || priceNum === 0;
      const inPromo = !isFree && isCourseInPromoPeriod(c);

      // Category filter
      if (selectedFinderCategory !== "all") {
        if (selectedFinderCategory === "management") {
          const match =
            courseName.includes("management") ||
            courseName.includes("wherehous") ||
            courseName.includes("warehouse") ||
            courseName.includes("business") ||
            courseName.includes("logistics") ||
            courseTags.includes("management") ||
            courseTags.includes("warehouse");
          if (!match) return false;
        } else if (selectedFinderCategory === "accounting") {
          const match =
            courseName.includes("accounting") ||
            courseName.includes("quickbooks") ||
            courseName.includes("quick books") ||
            courseName.includes("xero") ||
            courseName.includes("myob") ||
            courseName.includes("tax") ||
            courseName.includes("finance") ||
            courseTags.includes("accounting") ||
            courseTags.includes("quickbooks");
          if (!match) return false;
        } else if (selectedFinderCategory === "data-it") {
          const match =
            courseName.includes("excel") ||
            courseName.includes("power bi") ||
            courseName.includes("powerbi") ||
            courseName.includes("analytics") ||
            courseName.includes("data") ||
            courseTags.includes("data") ||
            courseTags.includes("power bi") ||
            courseTags.includes("excel");
          if (!match) return false;
        } else if (selectedFinderCategory === "ai") {
          const match =
            courseName.includes("ai") ||
            courseName.includes("intelligence") ||
            courseName.includes("chatgpt") ||
            courseName.includes("prompt") ||
            courseTags.includes("ai");
          if (!match) return false;
        } else if (selectedFinderCategory === "logistics") {
          const match =
            courseName.includes("logistics") ||
            courseName.includes("supply") ||
            courseName.includes("shipping") ||
            courseName.includes("wherehous") ||
            courseName.includes("warehouse") ||
            courseName.includes("import") ||
            courseName.includes("export") ||
            courseTags.includes("logistics") ||
            courseTags.includes("warehouse") ||
            courseTags.includes("supply");
          if (!match) return false;
        } else if (selectedFinderCategory === "design") {
          const match =
            courseName.includes("design") ||
            courseName.includes("web") ||
            courseName.includes("photoshop") ||
            courseName.includes("illustrator") ||
            courseName.includes("video") ||
            courseTags.includes("design") ||
            courseTags.includes("trainingandcourses");
          if (!match) return false;
        } else if (selectedFinderCategory === "architecture") {
          const match =
            courseName.includes("autocad") ||
            courseName.includes("architecture") ||
            courseName.includes("drafting") ||
            courseTags.includes("architecture");
          if (!match) return false;
        }
      }

      // Price filter
      if (priceFilter === "free" && !isFree) return false;
      if (priceFilter === "promo" && !inPromo) return false;
      if (priceFilter === "standard" && (isFree || inPromo)) return false;

      // Search query filter
      if (searchQuery.trim() !== "") {
        const query = searchQuery.toLowerCase().trim();
        const matchesDirect =
          courseName.includes(query) ||
          lecturerName.includes(query) ||
          courseTags.includes(query) ||
          courseDesc.includes(query);

        if (!matchesDirect) {
          const queryWords = query
            .split(/\s+/)
            .filter((w) => w.length > 2 && w !== "and" && w !== "for" && w !== "&");
          if (queryWords.length > 0) {
            const matchesWords = queryWords.some(
              (w) =>
                courseName.includes(w) ||
                lecturerName.includes(w) ||
                courseTags.includes(w)
            );
            if (!matchesWords) return false;
          } else {
            return false;
          }
        }
      }

      return true;
    });
  };

  const handleInquirySubmit = async (e) => {
    e.preventDefault();
    if (!inquiryName || !inquiryPhone || !inquiryMessage) {
      message.error("Please fill in all required fields (Name, Phone, and Message).");
      return;
    }

    setIsInquirySubmitting(true);
    try {
      const response = await submitInquiry({
        name: inquiryName,
        email: inquiryEmail || "not_provided@ibas.lk",
        phone: inquiryPhone,
        course: inquiryCourse || "General Inquiry",
        message: inquiryMessage
      });

      if (response && response.success) {
        Modal.success({
          title: "Inquiry Successfully Submitted!",
          content: (
            <div>
              <p>Thank you <strong>{inquiryName}</strong> for contacting IBAS Institute.</p>
              <p>An educational counselor will reach out to you at <strong>{inquiryPhone}</strong> shortly regarding the <strong>{inquiryCourse || "selected"}</strong> program.</p>
            </div>
          ),
          okText: "Done",
          onOk() {
            // Reset Form
            setInquiryName("");
            setInquiryEmail("");
            setInquiryPhone("");
            setInquiryCourse("");
            setInquiryMessage("");
          }
        });
      } else {
        message.error(response?.message || "Failed to submit inquiry. Please try again.");
      }
    } catch (error) {
      console.error("Submit inquiry error:", error);
      message.error("An unexpected error occurred. Please try again later.");
    } finally {
      setIsInquirySubmitting(false);
    }
  };

  const renderAcademicPanel = dynamicAcademicPanel.length > 0
    ? dynamicAcademicPanel.map(p => ({
      title: p.name,
      desc: p.description,
      icon: p.imageUrl || p.designation || "🎓"
    }))
    : academicPanel;

  const renderTestimonials = dynamicTestimonials.length > 0
    ? dynamicTestimonials.map((t, idx) => ({
      id: t.id || idx,
      content: t.feedback,
      author: t.name,
      course: t.role,
      rating: t.rating,
      image: t.avatarUrl || "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop"
    }))
    : testimonials;

  const currentCategoryObj = courseCategories.find(cat => cat.id === activeCategory) || courseCategories[0];

  return (
    <div className="landing-page" id="landinghome">
      <header className="site-header">
        <div className="header-conteiner">
          <div className="logo">
            <a href="/" aria-label="IBAS Home">
              <img src={logo} alt="IBAS Logo" />
            </a>
          </div>

          {/* Enhanced Mobile Header Actions: Quick LMS Pill + Animated Hamburger Toggle */}
          {isMobile && (
            <div className="mobile-header-actions-group">
              <Link to="/login" className="mobile-quick-header-cta lms-cta" aria-label="LMS Login">
                <UserOutlined style={{ fontSize: 12 }} />
                <span>LMS</span>
              </Link>
              <button
                type="button"
                className={`mobile-animated-hamburger-btn ${drawerVisible ? "is-open" : ""}`}
                onClick={drawerVisible ? closeDrawer : showDrawer}
                aria-label={drawerVisible ? "Close navigation menu" : "Open navigation menu"}
              >
                <div className="hamburger-bars-container">
                  <span className="bar bar-top" />
                  <span className="bar bar-middle" />
                  <span className="bar bar-bottom" />
                </div>
              </button>
            </div>
          )}

          {/* desktop navigation */}
          <div className="btns">
            <Button type="link" onClick={() => scrollToSection("landinghome")}>
              Home
            </Button>
            <Link to="/verify-certificate">
              <Button type="link">Verify Certificate</Button>
            </Link>
            <Button type="link" onClick={() => scrollToSection("course-finder")}>
              Course Finder
            </Button>
            <Popover
              content={renderNavbarCourseSteps()}
              trigger={["click", "hover"]}
              placement="bottom"
              open={navCoursesOpen}
              onOpenChange={setNavCoursesOpen}
              overlayClassName="navbar-courses-mega-popover"
            >
              <Button
                type="link"
                className="navbar-courses-tab-btn"
                onClick={() => setNavCoursesOpen(!navCoursesOpen)}
              >
                <span>Courses</span>
                <DownOutlined style={{ fontSize: 11, marginLeft: 4 }} />
              </Button>
            </Popover>
            <Button type="link" onClick={() => scrollToSection("faculties")}>
              Faculties
            </Button>
            <Button type="link" onClick={() => scrollToSection("professional-programs")}>
              Professional Programs
            </Button>
            <Button type="link" onClick={() => scrollToSection("blog-news")}>
              Blog
            </Button>
            <Button type="link" onClick={() => scrollToSection("contact-us")}>
              Contact Us
            </Button>
            <Link className="signin-btn" to="/register">
              Apply Now
            </Link>
            <Link className="signin-btn btn-solid-gold" to="/login">
              LMS Login
            </Link>
          </div>
        </div>

        {/* mobile drawer */}
        {isMobile && (
          <Drawer
            placement="right"
            closable={true}
            onClose={closeDrawer}
            open={drawerVisible}
            closeIcon={<CloseOutlined style={{ fontSize: 18, color: "#64748b" }} />}
            className="mobile-menu-drawer"
            width={screens.xs ? "85vw" : 300}
          >
            <div className="drawer-menu">
              <Button block type="link" onClick={() => scrollToSection("landinghome")}>
                <span>Home</span>
                <RightOutlined />
              </Button>

              <Link to="/verify-certificate" onClick={closeDrawer} className="drawer-link-btn">
                <Button block type="link">
                  <span>Verify Certificate</span>
                  <RightOutlined />
                </Button>
              </Link>

              <Button block type="link" onClick={() => scrollToSection("course-finder")}>
                <span>Course Finder</span>
                <RightOutlined />
              </Button>

              {/* Expandable Courses Item */}
              <div className="drawer-courses-accordion-wrapper">
                <Button
                  block
                  type="link"
                  className={`drawer-courses-trigger ${mobileNavCoursesExpanded ? "is-expanded" : ""}`}
                  onClick={() => {
                    const next = !mobileNavCoursesExpanded;
                    setMobileNavCoursesExpanded(next);
                    if (next && !mobileNavSelectedCategory) {
                      setMobileNavSelectedCategory(courseCategories[0]?.id || "management");
                    }
                  }}
                >
                  <span>Courses</span>
                  {mobileNavCoursesExpanded ? <DownOutlined /> : <RightOutlined />}
                </Button>

                {mobileNavCoursesExpanded && (
                  <div className="drawer-categories-subcontainer">
                    <div className="drawer-step-hint">
                      Select category to view courses:
                    </div>
                    <div className="drawer-categories-list">
                      {courseCategories.map((cat) => {
                        const isCatSelected = mobileNavSelectedCategory === cat.id;
                        const catCourses = getNavbarCoursesByCategory(cat.id);
                        return (
                          <div key={cat.id} className="drawer-cat-block">
                            <div
                              className={`drawer-cat-header ${isCatSelected ? "active" : ""}`}
                              onClick={() =>
                                setMobileNavSelectedCategory(isCatSelected ? null : cat.id)
                              }
                            >
                              <div className="drawer-cat-header-left">
                                <span className="cat-bullet">•</span>
                                <span className="cat-title">{cat.name}</span>
                              </div>
                              <div className="drawer-cat-header-right">
                                <span className="cat-pill-count">{catCourses.length}</span>
                                {isCatSelected ? (
                                  <DownOutlined style={{ fontSize: 10, color: "#2563eb" }} />
                                ) : (
                                  <RightOutlined style={{ fontSize: 10, color: "#94a3b8" }} />
                                )}
                              </div>
                            </div>

                            {isCatSelected && (
                              <div className="drawer-courses-sublist">
                                <div className="drawer-course-tap-note">
                                  Tap any course to select and view details:
                                </div>
                                {catCourses.map((c) => {
                                  const isSelected = selectedCourseId === c.id;
                                  return (
                                    <div
                                      key={c.id || c.name}
                                      className={`drawer-course-tap-card ${isSelected ? "selected-course" : ""}`}
                                      onClick={() => handleSelectCourseFromNavbar(c, cat.id)}
                                    >
                                      <div className="course-card-details">
                                        <div className="course-card-name">
                                          {c.name}
                                        </div>
                                        <div className="course-card-meta">
                                          {c.duration || "Flexible"} {c.lecturer ? `• ${c.lecturer}` : ""}
                                        </div>
                                      </div>
                                      <Tag
                                        color={isSelected ? "green" : c.price === "Free" ? "green" : "blue"}
                                        style={{ margin: 0, fontSize: 10, fontWeight: 700, flexShrink: 0 }}
                                      >
                                        {isSelected ? "Selected ✓" : c.price === "Free" ? "Free" : "Select →"}
                                      </Tag>
                                    </div>
                                  );
                                })}
                                <Button
                                  block
                                  size="small"
                                  type="primary"
                                  className="drawer-view-all-category-btn"
                                  onClick={() => handleSelectCategoryFromNavbar(cat.id)}
                                >
                                  View All {cat.name} Courses ({catCourses.length}) →
                                </Button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              <Button block type="link" onClick={() => scrollToSection("faculties")}>
                <span>Faculties</span>
                <RightOutlined />
              </Button>

              <Button block type="link" onClick={() => scrollToSection("professional-programs")}>
                <span>Professional Programs</span>
                <RightOutlined />
              </Button>

              <Button block type="link" onClick={() => scrollToSection("blog-news")}>
                <span>Blog & News</span>
                <RightOutlined />
              </Button>

              <Button block type="link" onClick={() => scrollToSection("contact-us")}>
                <span>Contact Us</span>
                <RightOutlined />
              </Button>

              <Link to="/register" onClick={closeDrawer} className="drawer-link-btn">
                <Button block type="link">
                  <span>Apply Now</span>
                  <RightOutlined />
                </Button>
              </Link>
            </div>

            <div className="drawer-footer">
              <Link to="/login" onClick={closeDrawer} style={{ display: "block", width: "100%", textDecoration: "none" }}>
                <Button block className="drawer-lms-login-btn">
                  LMS Login
                </Button>
              </Link>
            </div>
          </Drawer>
        )}
      </header>

      <div className="body">
        {/* HERO SECTION */}
        <section
          className="hero animate-fade-in"
          data-aos="fade-up"
          data-aos-duration="900"
        >
          <div className="hero-container">
            <div className="content">

              <h1 className="hero-title">
                Creating the <br />
                <span className="highlight">Challenging Professional</span><br />
                for a Better Working World
              </h1>

              <p className="hero-subtitle">
                Accredited & Recognized by AAT Sri Lanka
              </p>

              <div className="hero-features">
                <span className="hero-feature-pill">
                  <CheckCircleOutlined className="check-icon" /> Online & Physical
                </span>
                <span className="hero-feature-pill">
                  <CheckCircleOutlined className="check-icon" /> Expert Lecturers
                </span>
                <span className="hero-feature-pill">
                  <CheckCircleOutlined className="check-icon" /> Flexible Learning
                </span>
                <span className="hero-feature-pill">
                  <CheckCircleOutlined className="check-icon" /> AAT Accredited
                </span>
                <span className="hero-feature-pill">
                  <CheckCircleOutlined className="check-icon" /> Career Focused
                </span>
              </div>

              <div className="hero-buttons">
                <Link className="btn btn-primary" to="/register">
                  Apply Now
                </Link>
                <button className="btn btn-secondary" onClick={() => scrollToSection("course-finder")}>
                  Explore Courses
                </button>
                <a className="btn btn-seminar" href="https://wa.me/94712163163?text=I%20am%20interested%20in%20joining%20the%20next%20free%20seminar" target="_blank" rel="noreferrer">
                  <WhatsAppOutlined style={{ marginRight: '6px' }} /> Join Free Seminar
                </a>
              </div>
            </div>

            <div className="hero-right">
              <div className="image-wrapper">
                <img
                  src="https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=800&h=600&fit=crop"
                  alt="IBAS Professional Students"
                  className="hero-image"
                />
                <div className="floating-badge">
                  <div className="badge-icon">
                    <UserOutlined />
                  </div>
                  <div>
                    <strong>2,500+</strong>
                    <span>Active Leaders</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SMART COURSE SEARCH BAR & COURSE FINDER Mega-Menu Section */}
        <section
          id="course-finder"
          className="course-finder-section"
          data-aos="fade-up"
          data-aos-duration="800"
        >
          <div className="section-header">
            <h2>Find Your <span className="gradient-text">Ideal Course</span></h2>
            <p className="subtitle">Search, select a category, and explore modern professional modules aligned to your future career goals.</p>
          </div>

          <div className="finder-container">
            {/* DUAL MODE SELECTOR: CATEGORY COURSE FINDER vs PROFESSIONAL EXAMINATION FINDER */}
            <div className="finder-mode-selector-wrapper">
              <Button
                type={finderMode === 'category' ? 'primary' : 'default'}
                size="large"
                icon={<BookOutlined />}
                className={`finder-mode-tab-btn ${finderMode === 'category' ? 'active-mode' : ''}`}
                onClick={() => {
                  setFinderMode('category');
                  setSelectedFinderCategory('all');
                  setProfExamFilter('all');
                  setSearchQuery('');
                }}
              >
                Course Finder by Category
              </Button>

              <Button
                type={finderMode === 'professional-exams' ? 'primary' : 'default'}
                size="large"
                icon={<SafetyCertificateOutlined />}
                className={`finder-mode-tab-btn prof-exam-mode ${finderMode === 'professional-exams' ? 'active-mode' : ''}`}
                onClick={() => {
                  setFinderMode('professional-exams');
                  setSelectedFinderCategory('all');
                  setProfExamFilter('all');
                  setSearchQuery('');
                }}
              >
                🎓 Professional Examination Finder
              </Button>
            </div>

            {/* DEDICATED PROFESSIONAL EXAMINATION SUB-FILTERS */}
            {finderMode === 'professional-exams' && (
              <div
                style={{
                  background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)',
                  padding: '20px 24px',
                  borderRadius: 16,
                  border: '1px solid #ddd6fe',
                  marginBottom: 8,
                  textAlign: 'center',
                }}
                className="animate-fade-in"
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 8 }}>
                  <SafetyCertificateOutlined style={{ color: '#7c3aed', fontSize: 20 }} />
                  <h3 style={{ fontSize: '18px', fontWeight: 800, color: '#4c1d95', margin: 0 }}>
                    Professional Examination Preparatory Programs
                  </h3>
                </div>
                <p style={{ color: '#6d28d9', fontSize: '13px', maxWidth: 700, margin: '0 auto 16px auto' }}>
                  Accredited by standard licensing bodies. Select your examining body to see targeted revision, comprehensive stage modules, and exam intakes.
                </p>

                <div style={{ display: 'flex', justifyContent: 'center', gap: 8, flexWrap: 'wrap' }}>
                  {[
                    { key: 'all', label: 'All Professional Exams' },
                    { key: 'aat', label: 'AAT Sri Lanka (Level I, II, III)' },
                    { key: 'ca', label: 'Chartered Accounting (CA Sri Lanka)' },
                    { key: 'cma', label: 'CMA Sri Lanka' },
                    { key: 'slim', label: 'SLIM Qualifications' },
                  ].map((exam) => (
                    <Button
                      key={exam.key}
                      size="middle"
                      onClick={() => {
                        setProfExamFilter(exam.key);
                        scrollToSection("courses-section");
                      }}
                      style={{
                        borderRadius: 20,
                        fontWeight: 600,
                        fontSize: '12px',
                        background: profExamFilter === exam.key ? '#7c3aed' : '#ffffff',
                        borderColor: '#7c3aed',
                        color: profExamFilter === exam.key ? '#ffffff' : '#7c3aed',
                      }}
                    >
                      {exam.label}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* UDEMY STYLE CATEGORY PILLS BAR (Shown in Category mode) */}
            {finderMode === 'category' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div
                  style={{
                    display: 'flex',
                    gap: 8,
                    overflowX: 'auto',
                    paddingBottom: 6,
                    scrollbarWidth: 'thin',
                  }}
                  className="category-pills-row"
                >
                  <Button
                    size="middle"
                    onClick={() => {
                      setSelectedFinderCategory('all');
                      setActiveCategory('management');
                      scrollToSection("courses-section");
                    }}
                    style={{
                      borderRadius: 20,
                      fontWeight: 600,
                      fontSize: '13px',
                      flexShrink: 0,
                      background: selectedFinderCategory === 'all' ? '#03245e' : '#f8fafc',
                      borderColor: selectedFinderCategory === 'all' ? '#03245e' : '#e2e8f0',
                      color: selectedFinderCategory === 'all' ? '#ffffff' : '#475569',
                    }}
                  >
                    All Categories
                  </Button>
                  {courseCategories.map((cat) => (
                    <Button
                      key={cat.id}
                      size="middle"
                      onClick={() => {
                        setSelectedFinderCategory(cat.id);
                        setActiveCategory(cat.id);
                        scrollToSection("courses-section");
                      }}
                      style={{
                        borderRadius: 20,
                        fontWeight: 600,
                        fontSize: '13px',
                        flexShrink: 0,
                        background: selectedFinderCategory === cat.id ? '#03245e' : '#f8fafc',
                        borderColor: selectedFinderCategory === cat.id ? '#03245e' : '#e2e8f0',
                        color: selectedFinderCategory === cat.id ? '#ffffff' : '#475569',
                      }}
                    >
                      {cat.name}
                    </Button>
                  ))}
                </div>

                {/* Price Filter Chips */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '12px', fontWeight: 600, color: '#64748b' }}>Filter by Pricing:</span>
                  {[
                    { key: 'all', label: 'All Courses' },
                    { key: 'free', label: 'Free Seminars' },
                    { key: 'promo', label: 'Discount Offers' },
                    { key: 'standard', label: 'Standard Programs' },
                  ].map((p) => (
                    <Tag.CheckableTag
                      key={p.key}
                      checked={priceFilter === p.key}
                      onChange={() => {
                        setPriceFilter(priceFilter === p.key ? 'all' : p.key);
                        scrollToSection("courses-section");
                      }}
                      style={{
                        borderRadius: 12,
                        padding: '2px 10px',
                        fontSize: '12px',
                        fontWeight: 500,
                        border: '1px solid #cbd5e1',
                      }}
                    >
                      {p.label}
                    </Tag.CheckableTag>
                  ))}
                </div>
              </div>
            )}

            {/* SMART SEARCH BAR */}
            <div className="search-bar-wrapper">
              <form onSubmit={handleSearchSubmit} className="search-form">
                <Input
                  placeholder={
                    finderMode === 'professional-exams'
                      ? "Search Professional Exams... (e.g. AAT Level I, CA Business Level, CMA)"
                      : "Search Your Course Here... (e.g. QuickBooks, AI, Power BI, Logistics)"
                  }
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  prefix={<SearchOutlined className="search-icon" />}
                  size="large"
                  className="search-input"
                  allowClear
                />
                <Button type="primary" size="large" onClick={handleSearchSubmit} className="search-submit-btn">
                  Search
                </Button>
              </form>
              <div className="search-examples">
                <span className="label">Popular Searches:</span>
                <span className="tag" onClick={() => handleTagClick("QuickBooks")}>QuickBooks</span>
                <span className="tag" onClick={() => handleTagClick("AI")}>AI & ChatGPT</span>
                <span className="tag" onClick={() => handleTagClick("Power BI")}>Power BI</span>
                <span className="tag" onClick={() => handleTagClick("AAT")}>AAT Sri Lanka</span>
                <span className="tag" onClick={() => handleTagClick("Logistics")}>Logistics</span>
                <span className="tag" onClick={() => handleTagClick("Excel")}>Advanced Excel</span>
              </div>
            </div>

            {/* CATEGORIES Mega Menu Grid / Mobile Accordion */}
            {!isMobile ? (
              // DESKTOP SPLIT MEGA MENU
              <div className="desktop-mega-menu">
                <div className="categories-sidebar">
                  {courseCategories.map((cat) => (
                    <div
                      key={cat.id}
                      className={`category-item-link ${activeCategory === cat.id ? "active" : ""}`}
                      onMouseEnter={() => setActiveCategory(cat.id)}
                      onClick={() => setActiveCategory(cat.id)}
                    >
                      <div className="cat-name-icon">
                        {cat.icon}
                        <span>{cat.name}</span>
                      </div>
                      <RightOutlined className="chevron-icon" />
                    </div>
                  ))}
                </div>

                <div className="mega-menu-content-panel">
                  <div className="panel-header">
                    <h3>{currentCategoryObj.name}</h3>
                    <p className="panel-desc">{currentCategoryObj.description}</p>
                  </div>

                  <div className="subcourses-list-grid">
                    {currentCategoryObj.subCourses.map((sub, idx) => (
                      <div key={idx} className="subcourse-card" onClick={() => handleSubcourseClick(sub, currentCategoryObj.id)}>
                        <CheckCircleOutlined className="subcourse-icon" />
                        <span>{sub}</span>
                      </div>
                    ))}
                  </div>

                  <div className="panel-footer-actions">
                    <div className="related-badge-card">
                      <span className="badge-pill">Premium Curriculum</span>
                      <p>Mapped directly to Sri Lankan corporate specifications and recognized licensing boards.</p>
                    </div>
                    <Button
                      type="primary"
                      className="btn-solid-gold border-radius-8"
                      onClick={() => {
                        setSelectedFinderCategory(currentCategoryObj.id);
                        setSearchQuery("");
                        setFinderMode("category");
                        scrollToSection("courses-section");
                      }}
                    >
                      View {currentCategoryObj.name} Courses
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              // MOBILE ACCORDION COURSE MENUS
              <div className="mobile-accordion-list">
                {courseCategories.map((cat) => (
                  <div key={cat.id} className={`accordion-card ${expandedCategory === cat.id ? "expanded" : ""}`}>
                    <div
                      className="accordion-header"
                      onClick={() => setExpandedCategory(expandedCategory === cat.id ? null : cat.id)}
                    >
                      <div className="accordion-title-wrapper">
                        {cat.icon}
                        <span>{cat.name}</span>
                      </div>
                      <span className="accordion-indicator">
                        {expandedCategory === cat.id ? "−" : "+"}
                      </span>
                    </div>

                    {expandedCategory === cat.id && (
                      <div className="accordion-content animate-slide-down">
                        <p className="accordion-desc">{cat.description}</p>
                        <div className="mobile-subcourses-list">
                          {cat.subCourses.map((sub, idx) => (
                            <div key={idx} className="mobile-subcourse-item" onClick={() => handleSubcourseClick(sub, cat.id)}>
                              <span>{sub}</span>
                              <RightOutlined style={{ fontSize: '10px' }} />
                            </div>
                          ))}
                        </div>
                        <Button
                          type="primary"
                          block
                          className="accordion-cta btn-solid-gold"
                          onClick={() => {
                            setSelectedFinderCategory(cat.id);
                            setSearchQuery("");
                            setFinderMode("category");
                            scrollToSection("courses-section");
                          }}
                        >
                          View Courses
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* POPULAR COURSES SECTION */}
        <section
          id="courses-section"
          className="courses"
          data-aos="fade-up"
          data-aos-duration="800"
          data-aos-offset="100"
        >
          <div className="featured-courses">
            <h2>
              {finderMode === "professional-exams" ? (
                <>Professional <span className="gradient-text">Exam Preparatory Programs</span></>
              ) : (
                <>Our <span className="gradient-text">Popular Courses</span></>
              )}
            </h2>
            <p className="para">
              {finderMode === "professional-exams"
                ? "Accredited tuition and revision coaching for AAT Sri Lanka, CA Sri Lanka, CMA, and SLIM qualifications."
                : "Expand your accounting, finance, logistics, IT, and AI skillset with hands-on professional coaching."
              }
            </p>

            {/* ACTIVE FILTERS CHIP BAR */}
            {(searchQuery || selectedFinderCategory !== "all" || priceFilter !== "all" || finderMode === "professional-exams") && (
              <div className="active-filters-bar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, flexWrap: 'wrap', margin: '14px 0 6px 0' }}>
                <span style={{ fontSize: '12px', fontWeight: 600, color: '#64748b' }}>Active Filters:</span>

                {finderMode === "professional-exams" && (
                  <Tag color="purple" closable onClose={() => { setFinderMode("category"); setProfExamFilter("all"); }}>
                    Mode: Professional Exams {profExamFilter !== "all" ? `(${profExamFilter.toUpperCase()})` : ""}
                  </Tag>
                )}

                {selectedFinderCategory !== "all" && finderMode !== "professional-exams" && (
                  <Tag color="blue" closable onClose={() => setSelectedFinderCategory("all")}>
                    Category: {courseCategories.find(c => c.id === selectedFinderCategory)?.name || selectedFinderCategory}
                  </Tag>
                )}

                {priceFilter !== "all" && (
                  <Tag color="orange" closable onClose={() => setPriceFilter("all")}>
                    Pricing: {priceFilter === "free" ? "Free Seminars" : priceFilter === "promo" ? "Discount Offers" : "Standard"}
                  </Tag>
                )}

                {searchQuery && (
                  <Tag color="cyan" closable onClose={() => setSearchQuery("")}>
                    Keyword: "{searchQuery}"
                  </Tag>
                )}

                <Button type="link" size="small" onClick={handleResetAllFilters} style={{ fontWeight: 600, color: '#dc2626' }}>
                  Clear All Filters
                </Button>
              </div>
            )}
          </div>

          <div className="cources-body">
            <Spin tip="Loading Courses..." spinning={isLoading}>
              {getFilteredCourses().length > 0 ? (
                <List
                  grid={{
                    gutter: 30,
                    xs: 1,
                    sm: 1,
                    md: 2,
                    lg: 2,
                    xl: 3,
                    xxl: 3,
                  }}
                  dataSource={getFilteredCourses()}
                  renderItem={(item) => (
                    <List.Item>
                      <div
                        id={`course-card-${item.id}`}
                        className={`course-card ${selectedCourseId === item.id ? "selected-nav-course-highlight" : ""}`}
                      >
                        {selectedCourseId === item.id && (
                          <div className="selected-course-banner-badge">
                            ★ Selected Course
                          </div>
                        )}
                        <div className="card-image-container">
                          <img
                            src={
                              item?.coverImage
                                ? item?.coverImage
                                : DEFAULT_COURSE_COVER_IMAGE
                            }
                            alt={item?.name || "Course Cover"}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = "https://images.unsplash.com/photo-1501504905252-473c47e087f8?w=600&h=400&fit=crop";
                            }}
                          />
                          <div className="course-tag-pill">
                            <ClockCircleOutlined style={{ marginRight: "5px" }} />
                            {getCourseDuration(item)}
                          </div>
                        </div>
                        <div className="card-body">
                          <div className="card-top-meta">
                            <div className="course-time-period">
                              <ClockCircleOutlined className="time-icon" />
                              <span className="time-label">Time Period:</span>
                              <span className="time-value">{getCourseDuration(item)}</span>
                            </div>

                            {item?.lecturer && (
                              <div className="author">
                                <img
                                  src={
                                    item?.lecturerProfileImage || item?.createdBy?.profileImage || DEFAULT_USER_PROFILE_IMAGE
                                  }
                                  alt={item?.lecturer}
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=100&h=100&fit=crop";
                                  }}
                                />
                                <h3 title={item?.lecturer}>
                                  {item?.lecturer}
                                </h3>
                              </div>
                            )}
                          </div>

                          <h3 className="title" title={item?.name}>{item?.name}</h3>

                          {item?.description && (
                            <div className="course-card-desc-wrapper" style={{ margin: '8px 0 10px 0' }}>
                              <div style={{
                                color: '#475569',
                                fontSize: '12.5px',
                                lineHeight: 1.5,
                              }}>
                                {expandedDescMap[item.id] ? (
                                  <span>{stripHtml(item.description)}</span>
                                ) : (
                                  <span>
                                    {stripHtml(item.description).length > 150
                                      ? `${stripHtml(item.description).slice(0, 150)}...`
                                      : stripHtml(item.description)
                                    }
                                  </span>
                                )}
                              </div>
                              {stripHtml(item.description).length > 150 && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleDesc(item.id);
                                  }}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    padding: 0,
                                    color: '#0284c7',
                                    cursor: 'pointer',
                                    fontWeight: 600,
                                    fontSize: '12px',
                                    marginTop: 3,
                                    display: 'inline-block',
                                  }}
                                >
                                  {expandedDescMap[item.id] ? "Show Less ↑" : "Read More →"}
                                </button>
                              )}
                            </div>
                          )}

                          <div className="card-pricing-section">
                            {(() => {
                              const priceNum = Number(item?.price);
                              const promoNum = Number(item?.promoPrice);
                              const isFree = item?.price === 0 || item?.price === "Free" || priceNum === 0;
                              const inPromoPeriod = !isFree && isCourseInPromoPeriod(item);

                              if (typeof item?.price === "string" && isNaN(priceNum) && item.price !== "Free") {
                                return (
                                  <div className="course-pricing-box standard">
                                    <div className="price-col standard-col">
                                      <span className="price-tag-label">Pricing Structure:</span>
                                      <span className="price-main-val primary" style={{ fontSize: '1.1rem' }}>{item.price}</span>
                                    </div>
                                  </div>
                                );
                              }

                              if (isFree) {
                                return (
                                  <div className="course-pricing-box free">
                                    <div className="price-item free-item">
                                      <span className="price-tag-label">Course Price:</span>
                                      <span className="free-badge">Free Seminar</span>
                                    </div>
                                  </div>
                                );
                              }

                              if (inPromoPeriod) {
                                const discountDiff = priceNum - promoNum;
                                const discountPercent = Math.round((discountDiff / priceNum) * 100);
                                return (
                                  <div className="course-pricing-box discounted">
                                    <div className="pricing-split-row">
                                      <div className="price-col discounted-col">
                                        <span className="price-tag-label">Discounted Price:</span>
                                        <span className="price-main-val highlight">{formatLKR(promoNum)}</span>
                                      </div>
                                      <div className="price-col original-col">
                                        <span className="price-tag-label">Course Price:</span>
                                        <span className="price-main-val strikethrough"><del>{formatLKR(priceNum)}</del></span>
                                      </div>
                                    </div>
                                    <div className="discount-footer-row">
                                      <span className="discount-save-tag">Save {formatLKR(discountDiff)} ({discountPercent}% OFF)</span>
                                      <PromoCountdown endDate={item.promoEndDate} />
                                    </div>
                                  </div>
                                );
                              }

                              return (
                                <div className="course-pricing-box standard">
                                  <div className="price-col standard-col">
                                    <span className="price-tag-label">Course Price:</span>
                                    <span className="price-main-val primary">{formatLKR(priceNum)}</span>
                                  </div>
                                  {item?.alumniDiscount > 0 && !isNaN(Number(item.alumniDiscount)) && (
                                    <div className="alumni-row">
                                      <span className="alumni-badge">
                                        Alumni Price: {formatLKR(priceNum - Number(item.alumniDiscount))}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        </div>

                        <div className="card-footer" style={{ padding: '0 20px 20px 20px' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
                            <Link
                              className="view-btn"
                              to={`/register`}
                              style={{ width: '100%', textDecoration: 'none' }}
                            >
                              <Button
                                type="primary"
                                block
                                style={{
                                  background: '#03245e',
                                  borderColor: '#03245e',
                                  color: '#fff',
                                  fontWeight: 700,
                                  height: 40,
                                  borderRadius: 8,
                                  fontSize: '14px',
                                  boxShadow: '0 4px 12px rgba(3, 36, 94, 0.12)',
                                }}
                              >
                                {Number(item?.price) === 0 || item?.price === "Free" ? "Join Free Seminar →" : "Enroll Now →"}
                              </Button>
                            </Link>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, width: '100%' }}>
                              <Button
                                type="default"
                                icon={<PlayCircleOutlined style={{ color: '#0284c7' }} />}
                                onClick={() => handleOpenCoursePreview(item)}
                                style={{
                                  borderColor: '#bae6fd',
                                  background: '#f0f9ff',
                                  color: '#03245e',
                                  fontWeight: 600,
                                  height: 36,
                                  borderRadius: 8,
                                  fontSize: '12px',
                                  padding: '0 4px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                Preview
                              </Button>

                              {item?.whatsappGroupLink ? (
                                <a
                                  href={item?.whatsappGroupLink}
                                  target="_blank"
                                  rel="noreferrer"
                                  style={{ textDecoration: 'none' }}
                                >
                                  <Button
                                    type="default"
                                    icon={<WhatsAppOutlined style={{ color: '#16a34a' }} />}
                                    style={{
                                      borderColor: '#bbf7d0',
                                      background: '#f0fdf4',
                                      color: '#15803d',
                                      fontWeight: 600,
                                      height: 36,
                                      borderRadius: 8,
                                      fontSize: '12px',
                                      padding: '0 4px',
                                      width: '100%',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    }}
                                  >
                                    WhatsApp
                                  </Button>
                                </a>
                              ) : (
                                <a
                                  href={`https://wa.me/94712163163?text=I%20want%20to%20enroll%20in%20${encodeURIComponent(item?.name)}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  style={{ textDecoration: 'none' }}
                                >
                                  <Button
                                    type="default"
                                    icon={<WhatsAppOutlined style={{ color: '#16a34a' }} />}
                                    style={{
                                      borderColor: '#bbf7d0',
                                      background: '#f0fdf4',
                                      color: '#15803d',
                                      fontWeight: 600,
                                      height: 36,
                                      borderRadius: 8,
                                      fontSize: '12px',
                                      padding: '0 4px',
                                      width: '100%',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    }}
                                  >
                                    Inquire
                                  </Button>
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </List.Item>
                  )}
                />
              ) : (
                <div className="no-courses-found-box">
                  <p>
                    {searchQuery
                      ? <>No courses match your filter keyword <strong>"{searchQuery}"</strong>.</>
                      : <>No courses match the currently selected filter criteria.</>
                    }
                  </p>
                  <Button
                    type="primary"
                    onClick={handleResetAllFilters}
                    className="btn-solid-gold"
                  >
                    Reset All Filters & View All Courses
                  </Button>
                </div>
              )}
            </Spin>
          </div>
        </section>

        {/* WHY CHOOSE IBAS SECTION (8 standard items) */}
        <section id="features-heading" className="courses margin_low">
          <div
            className="features-heading"
            data-aos="fade-up"
            data-aos-duration="800"
            data-aos-offset="100"
          >
            <h2>
              Why Choose <span className="gradient-text">IBAS</span>
            </h2>
            <p className="para">
              Gateway to Professional Business & Accounting Excellence
            </p>
            <p>
              Institute of Business and Accounting Studies (Pvt) Ltd (IBAS) is a government-registered higher education institute (PV No: 00264413) dedicated to bridging the academic-professional gap by compiling specialized, employer-aligned learning structures with flexible modules.
            </p>
          </div>

          <div className="cources-body">
            <List
              grid={{
                gutter: 30,
                xs: 1,
                sm: 1,
                md: 2,
                lg: 3,
                xl: 4,
                xxl: 4,
              }}
              dataSource={specifications}
              renderItem={(item, index) => (
                <List.Item>
                  <div
                    key={item?.title}
                    className="list-card"
                    data-aos="fade-up"
                    data-aos-delay={index * 100}
                    data-aos-once="true"
                  >
                    <div className="icon-wrapper">{item?.icon}</div>
                    <h3>{item?.title}</h3>
                    <div className="description">
                      <p>{item?.description}</p>
                    </div>
                  </div>
                </List.Item>
              )}
            />
          </div>
        </section>

        {/* FACULTIES SECTION */}
        <section
          id="faculties"
          className="faculties-section"
          data-aos="fade-up"
          data-aos-duration="800"
        >
          <div className="section-header">
            <h2>Our Highly Distinguished <span className="gradient-text">Academic Panel</span></h2>
            <p className="subtitle">IBAS panel draws together eminent educators, industry leaders, and certified practitioners representing standard committees.</p>
          </div>

          <div className="faculties-grid-container">
            {renderAcademicPanel.map((panel, idx) => (
              <div key={idx} className="faculty-card" data-aos="zoom-in" data-aos-delay={idx * 50}>
                <div className="faculty-emoji-header">
                  {typeof panel.icon === "string" && (panel.icon.startsWith("http") || panel.icon.startsWith("data:") || panel.icon.includes("/")) ? (
                    <img src={panel.icon} alt={panel.title} className="faculty-image-avatar" style={{ width: "50px", height: "50px", borderRadius: "50%", objectFit: "cover" }} />
                  ) : (
                    panel.icon
                  )}
                </div>
                <h3>{panel.title}</h3>
                <p>{panel.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* VIDEO INTRODUCTION SECTION */}
        <section
          className="video-intro-section"
          data-aos="fade-up"
          data-aos-duration="850"
        >
          <div className="video-container-card">
            <div className="video-text-content">
              <h2>Watch Our <span className="gold-text">Campus Introduction</span></h2>
              <p>Take a virtual tour of our modern facilities at Gampaha, see our online webinar setups, meet our Senior Chartered Accounting lectures, and hear real student success stories.</p>
              <div className="bullets-panel">
                <p>✓ Guided physical campus walkthrough</p>
                <p>✓ Preview of the student LMS dashboard</p>
                <p>✓ Sneak-peak into live QuickBooks classes</p>
              </div>
              <Button type="primary" size="large" icon={<PlayCircleOutlined />} onClick={() => setVideoModalVisible(true)} className="play-button-gold">
                Watch Introduction Video
              </Button>
            </div>
            <div className="video-thumbnail-side" onClick={() => setVideoModalVisible(true)}>
              <div className="thumbnail-overlay">
                <PlayCircleOutlined className="giant-play-icon" />
                <span>Click to Play Intro (3:45)</span>
              </div>
            </div>
          </div>
        </section>

        {/* PROFESSIONAL PROGRAMS SECTION */}
        <section
          id="professional-programs"
          className="professional-programs-section"
          data-aos="fade-up"
          data-aos-duration="800"
        >
          <div className="section-header">
            <h2>Professional <span className="gradient-text">Exam Programs</span></h2>
            <p className="subtitle">We prepare students for prestigious local and international corporate examinations with verified pass rates.</p>
          </div>

          <div className="programs-layout-grid">
            <div className="program-badge-box">
              <div className="pro-header">AAT Sri Lanka</div>
              <p>Recognized and accredited by the Association of Accounting Technicians of Sri Lanka. Comprehensive study kits, calculations practices, and mock exams.</p>
              <Link to="/register" className="pro-link">Enroll in AAT Classes →</Link>
            </div>

            <div className="program-badge-box">
              <div className="pro-header">Chartered Accounting (CA)</div>
              <p>Preparatory coaching for local Chartered Accountant exams. Managed entirely by Senior Chartered Accountants (FCA/ACA) with real workplace files.</p>
              <Link to="/register" className="pro-link">Enroll in CA Classes →</Link>
            </div>

            <div className="program-badge-box">
              <div className="pro-header">CMA Sri Lanka</div>
              <p>Certified Management Accountants (CMA) preparatory coaching. Specialize in managerial finance, strategic risk, and business performance management.</p>
              <Link to="/register" className="pro-link">Enroll in CMA Classes →</Link>
            </div>

            <div className="program-badge-box">
              <div className="pro-header">SLIM Qualifications</div>
              <p>Sri Lanka Institute of Marketing (SLIM) coaching programs for aspiring marketing executives, brand managers, and sales entrepreneurs.</p>
              <Link to="/register" className="pro-link">Enroll in SLIM Classes →</Link>
            </div>
          </div>
        </section>

        {/* STATS SECTION */}
        <section
          className="stats-section"
          data-aos="fade-up"
          data-aos-duration="800"
          data-aos-offset="100"
        >
          <div className="stats-container">
            <div className="stats-grid">
              {statsData.map((s, i) => (
                <div
                  key={i}
                  className="stats-card"
                  data-aos="fade-up"
                  data-aos-delay={i * 100}
                  data-aos-once="true"
                >
                  <div className="stats-icon">{s.icon}</div>
                  <div className="stats-text">
                    <p className="stats-value">{s.value}</p>
                    <p className="stats-label">{s.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* TESTIMONIALS SECTION */}
        <section className="testimonials-section">
          <div
            className="container"
            data-aos="fade-up"
            data-aos-duration="800"
            data-aos-offset="100"
          >
            <h2 className="testimonials-title">
              What Our <span className="gradient-text">Students Say</span>
            </h2>
            <p className="testimonials-subtitle">
              Hear directly from certified graduates who transformed their careers with IBAS Institute.
            </p>

            <div className="testimonial-carousel-wrapper">
              <button
                className="arrow-btn prev-btn"
                onClick={() => carouselRef.current.prev()}
              >
                <LeftOutlined />
              </button>

              <Carousel
                ref={carouselRef}
                autoplay
                dots
                arrows={false}
              >
                {renderTestimonials.map((t) => (
                  <div key={t.id}>
                    <div className="testimonial-card">
                      <div className="rating-stars-wrapper">
                        {[...Array(t.rating)].map((_, i) => (
                          <StarFilled key={i} className="gold-star-icon" />
                        ))}
                      </div>
                      <p className="testimonial-content">"{t.content}"</p>
                      <div className="testimonial-author">
                        <img
                          src={t.image}
                          alt={t.author}
                          className="author-avatar"
                        />
                        <div className="author-info">
                          <h4 className="author-name">{t.author}</h4>
                          <p className="author-role">
                            Completed: <strong>{t.course}</strong>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </Carousel>

              <button
                className="arrow-btn next-btn"
                onClick={() => carouselRef.current.next()}
              >
                <RightOutlined />
              </button>
            </div>
          </div>
        </section>

        {/* BLOG & NEWS SECTION */}
        <section
          id="blog-news"
          className="blog-news-section"
          data-aos="fade-up"
          data-aos-duration="800"
        >
          <div className="section-header">
            <h2>Blog & <span className="gradient-text">Institute News</span></h2>
            <p className="subtitle">Get the latest exam notices, structural updates, accounting guides, and technology integration trends.</p>
          </div>

          <div className="blog-posts-grid">
            <div className="blog-card" data-aos="fade-up" data-aos-delay="0">
              <div className="blog-img-wrapper">
                <img src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=600&h=400&fit=crop" alt="AAT Sri Lanka Exam Prep Tips" />
              </div>
              <div className="blog-body">
                <span className="blog-date">May 12, 2026 • Academic Notices</span>
                <h3>Top Tips to Clear AAT Level III Calculation Papers</h3>
                <p>Calculations in AAT can be daunting. Our Senior Chartered Accounting lecturers share strategies to score full marks in audit equations...</p>
                <Link to="/register" className="blog-read-more">Read Full Article →</Link>
              </div>
            </div>

            <div className="blog-card" data-aos="fade-up" data-aos-delay="100">
              <div className="blog-img-wrapper">
                <img src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=400&fit=crop" alt="Power BI dashboards in accounting" />
              </div>
              <div className="blog-body">
                <span className="blog-date">April 28, 2026 • Technology Trends</span>
                <h3>Why Every Modern Accountant Needs Power BI Skills</h3>
                <p>Simple static balance sheets are no longer enough for boardrooms. Visual dashboard reporting is the new global standard for corporate analysts...</p>
                <Link to="/register" className="blog-read-more">Read Full Article →</Link>
              </div>
            </div>

            <div className="blog-card" data-aos="fade-up" data-aos-delay="200">
              <div className="blog-img-wrapper">
                <img src="https://images.unsplash.com/photo-1677442136019-21780efad99a?w=600&h=400&fit=crop" alt="AI in Sri Lankan Business" />
              </div>
              <div className="blog-body">
                <span className="blog-date">March 15, 2026 • Skill Development</span>
                <h3>Integrating Artificial Intelligence into SME Audits</h3>
                <p>AI prompt engineering tools are automating spreadsheet structures. See how local companies use ChatGPT to audit transaction logs in minutes...</p>
                <Link to="/register" className="blog-read-more">Read Full Article →</Link>
              </div>
            </div>
          </div>
        </section>

        {/* CTA SECTION */}
        <section className="cta-section">
          <div
            className="section-container"
            data-aos="fade-up"
            data-aos-duration="800"
            data-aos-offset="100"
          >
            <div className="cta-content">
              <div className="cta-image-wrapper">
                <img src={cta} alt="Start learning today at IBAS" />
              </div>
              <div className="cta-text-wrapper">
                <h2 className="cta-title">
                  Ready to <span className="gradient-text">Advance Your Career?</span>
                </h2>
                <p className="cta-description">
                  Join thousands of working professionals and students who have already qualified their accredited accounting certifications and practical IT capabilities with IBAS.
                </p>
                <ul className="cta-features-list">
                  {features.map((feat, idx) => (
                    <li key={idx}>
                      <svg
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        className="cta-icon"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span>{feat}</span>
                    </li>
                  ))}
                </ul>
                <div className="cta-buttons">
                  <Link className="cta-button animate-pulse" to="/register">
                    Register Now
                  </Link>
                  <button className="secondary-button" onClick={() => scrollToSection("course-finder")}>
                    Browse Categories
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CONTACT US PAGE SECTION */}
        <section
          id="contact-us"
          className="contact-us-section"
          data-aos="fade-up"
          data-aos-duration="850"
        >
          <div className="section-header">
            <h2>Contact <span className="gradient-text">IBAS Institute</span></h2>
            <p className="subtitle">Have inquiries about registration, fees, or class schedules? Send us a message or visit our Gampaha Head Office.</p>
          </div>

          <div className="contact-grid-container">
            {/* INQUIRY FORM */}
            <div className="inquiry-form-card">
              <h3>Submit an Inquiry</h3>
              <p>Complete the form below and an enrollment officer will respond within 24 hours.</p>
              <form onSubmit={handleInquirySubmit} className="inquiry-form-fields">
                <div className="form-group">
                  <label htmlFor="name">Full Name <span className="required">*</span></label>
                  <Input
                    id="name"
                    required
                    placeholder="Enter your name"
                    value={inquiryName}
                    onChange={(e) => setInquiryName(e.target.value)}
                    size="large"
                  />
                </div>
                <div className="form-group-row">
                  <div className="form-group">
                    <label htmlFor="email">Email Address</label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="name@example.com"
                      value={inquiryEmail}
                      onChange={(e) => setInquiryEmail(e.target.value)}
                      size="large"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="phone">Mobile Number <span className="required">*</span></label>
                    <Input
                      id="phone"
                      required
                      placeholder="e.g. 0712163163"
                      value={inquiryPhone}
                      onChange={(e) => setInquiryPhone(e.target.value)}
                      size="large"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="course">Course of Interest</label>
                  <select
                    id="course"
                    className="custom-form-select"
                    value={inquiryCourse}
                    onChange={(e) => setInquiryCourse(e.target.value)}
                  >
                    <option value="">-- Select Course (or pick from menu) --</option>
                    {courseCategories.map((cat) => {
                      const catCourses = getNavbarCoursesByCategory(cat.id);
                      if (!catCourses || catCourses.length === 0) return null;
                      return (
                        <optgroup key={cat.id} label={cat.name}>
                          {catCourses.map((c) => (
                            <option key={c.id || c.name} value={c.name}>
                              {c.name}
                            </option>
                          ))}
                        </optgroup>
                      );
                    })}
                    <option value="General Inquiry">General Educational Counseling / Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="message">Your Message <span className="required">*</span></label>
                  <Input.TextArea
                    id="message"
                    required
                    placeholder="Enter details of your inquiry..."
                    value={inquiryMessage}
                    onChange={(e) => setInquiryMessage(e.target.value)}
                    rows={4}
                  />
                </div>
                <Button type="primary" htmlType="submit" size="large" className="submit-btn-gold" loading={isInquirySubmitting}>
                  Submit Inquiry
                </Button>
              </form>
            </div>

            {/* CONTACT DETAILS & GOOGLE MAPS */}
            <div className="contact-info-panel-side">
              <div className="info-card">
                <h3>Office Details</h3>

                <div className="info-row">
                  <EnvironmentTwoTone className="info-icon" twoToneColor="#f0b323" />
                  <div>
                    <h4>Head Office</h4>
                    <p>No. 04, Bauddhaloka Mawatha, Gampaha, Sri Lanka</p>
                  </div>
                </div>

                <div className="info-row">
                  <PhoneTwoTone className="info-icon" twoToneColor="#f0b323" />
                  <div>
                    <h4>Hotlines</h4>
                    <p>071 216 3163 | 033 232 1966</p>
                  </div>
                </div>

                <div className="info-row">
                  <MailTwoTone className="info-icon" twoToneColor="#f0b323" />
                  <div>
                    <h4>Email Address</h4>
                    <p><a href="mailto:ibasinstitute@gmail.com">ibasinstitute@gmail.com</a></p>
                  </div>
                </div>

                <div className="whatsapp-direct-box">
                  <p>Prefer instant messaging? Chat directly with an enrollment counselor on WhatsApp.</p>
                  <a href="https://wa.me/94712163163" target="_blank" rel="noreferrer" className="whatsapp-big-btn">
                    <WhatsAppOutlined /> Message Us on WhatsApp
                  </a>
                </div>
              </div>

              {/* GOOGLE MAPS EMBED */}
              <div className="google-map-container">
                <iframe
                  title="IBAS Google Map Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3959.0494488349247!2d79.99596327475855!3d7.097063492906325!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ae2fb87b9264fa7%3A0xe21262d083e913a!2sBauddhaloka%20Mawatha%2C%20Gampaha%2011000%2C%20Sri%20Lanka!5e0!3m2!1sen!2sua!4v1716124500000!5m2!1sen!2sua"
                  width="100%"
                  height="250"
                  style={{ border: 0, borderRadius: '12px' }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
            </div>
          </div>
        </section>
      </div>

      <footer id="footer">
        <div className="footer-sec">
          <div className="logo">
            <a href="/">
              <img src={logo} alt="IBAS logo" />
            </a>
          </div>
          <div className="text">
            <p>
              Institute of Business and Accounting Studies (Pvt) Ltd (IBAS) is a government-registered higher education institute (PV No: 00264413), recognized and accredited by AAT Sri Lanka. We develop industry-ready leaders via premium interactive accounting, management, and technology systems.
            </p>
          </div>
          <div className="social-links-row">
            <a href="https://facebook.com" target="_blank" rel="noreferrer" className="social-circle"><FacebookOutlined /></a>
            <a href="https://linkedin.com" target="_blank" rel="noreferrer" className="social-circle"><LinkedinOutlined /></a>
            <a href="https://twitter.com" target="_blank" rel="noreferrer" className="social-circle"><TwitterOutlined /></a>
            <a href="https://youtube.com" target="_blank" rel="noreferrer" className="social-circle"><YoutubeOutlined /></a>
          </div>
        </div>

        <div className="footer-sec">
          <h3>Quick Links</h3>
          <div className="text footer-links">
            <p>
              <Button type="link" onClick={() => scrollToSection("landinghome")}>
                Home
              </Button>
            </p>
            <p>
              <Link to="/verify-certificate">
                <Button type="link">Verify Certificate</Button>
              </Link>
            </p>
            <p>
              <Button type="link" onClick={() => scrollToSection("course-finder")}>
                Course Finder mega-menu
              </Button>
            </p>
            <p>
              <Button type="link" onClick={() => scrollToSection("courses-section")}>
                Popular Courses
              </Button>
            </p>
            <p>
              <Button type="link" onClick={() => scrollToSection("faculties")}>
                distinguished Faculty
              </Button>
            </p>
            <p>
              <Button type="link" onClick={() => scrollToSection("professional-programs")}>
                Accredited Exam prep
              </Button>
            </p>
            <p>
              <Link to="/login">
                <Button type="link">LMS Login Portal</Button>
              </Link>
            </p>
          </div>
        </div>

        <div className="footer-sec">
          <h3>Gampaha Head Office</h3>
          <div className="text text-contacts">
            <p>
              <span>
                <EnvironmentTwoTone twoToneColor="#f0b323" />
              </span>
              No. 04, Bauddhaloka Mawatha, Gampaha, Sri Lanka.
            </p>
            <p>
              <span>
                <PhoneTwoTone twoToneColor="#f0b323" />
              </span>
              071 216 3163 | 033 232 1966
            </p>
            <p>
              <span>
                <MailTwoTone twoToneColor="#f0b323" />
              </span>
              <a href="mailto:ibasinstitute@gmail.com">ibasinstitute@gmail.com</a>
            </p>
          </div>
        </div>
      </footer>

      <section className="copy-right">
        <p>
          © 2026
          <a href="http://ibas.lk/" target="_blank" rel="noreferrer">
            {" "}
            IBAS.
          </a>{" "}
          All Rights Reserved.
        </p>

        <p>
          Designed & Developed by
          <a href="https://lionbit.cc/" target="_blank" rel="noreferrer">
            {" "}
            LionBit Solutions.
          </a>
        </p>
      </section>

      {/* VIDEO PREVIEW MODAL */}
      <Modal
        title="IBAS Institute - Campus Introduction"
        open={videoModalVisible}
        onCancel={() => setVideoModalVisible(false)}
        footer={null}
        width={800}
        destroyOnClose
      >
        <div className="video-player-container">
          <iframe
            width="100%"
            height="450"
            src="https://www.youtube.com/embed/dQw4w9WgXcQ?autoplay=1"
            title="YouTube video player"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        </div>
      </Modal>

      {/* PUBLIC COURSE PREVIEW & FREE VIDEOS MODAL (Accessible Without Registration) */}
      <Modal
        title={null}
        open={coursePreviewModalVisible}
        onCancel={() => {
          setCoursePreviewModalVisible(false);
          setActivePreviewVideo("");
        }}
        footer={null}
        width={screens.xs ? "96vw" : 960}
        destroyOnClose
        centered
        className="public-course-preview-modal"
      >
        {selectedCourseForPreview && (
          <div className="preview-modal-content" style={{ padding: '8px 4px' }}>
            {/* Modal Top Header */}
            <div
              className="preview-header-bar"
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                borderBottom: '1px solid #f1f5f9',
                paddingBottom: 16,
                marginBottom: 20,
                flexWrap: 'wrap',
                gap: 12,
              }}
            >
              <div style={{ flex: 1, minWidth: 260 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
                  {selectedCourseForPreview?.courseid && (
                    <Tag color="geekblue" style={{ fontWeight: 600 }}>{selectedCourseForPreview.courseid}</Tag>
                  )}
                  <Tag color="purple" style={{ fontWeight: 600 }}>
                    <ClockCircleOutlined style={{ marginRight: 4 }} />
                    {getCourseDuration(selectedCourseForPreview)}
                  </Tag>
                  {selectedCourseForPreview?.status && (
                    <Tag color="green">{String(selectedCourseForPreview.status).toUpperCase()}</Tag>
                  )}
                  {Number(selectedCourseForPreview?.price) === 0 && (
                    <Tag color="gold" style={{ fontWeight: 700 }}>FREE SEMINAR</Tag>
                  )}
                </div>
                <h2 style={{ fontSize: '22px', fontWeight: 800, color: '#03245e', margin: 0, lineHeight: 1.3 }}>
                  {selectedCourseForPreview.name}
                </h2>
                {selectedCourseForPreview?.lecturer && (
                  <p style={{ color: '#64748b', fontSize: '13px', margin: '6px 0 0 0' }}>
                    Instructor: <strong style={{ color: '#1e293b' }}>{selectedCourseForPreview.lecturer}</strong>
                  </p>
                )}
              </div>
              <div style={{ textAlign: isMobile ? 'left' : 'right' }}>
                <div style={{ fontSize: '20px', fontWeight: 800, color: '#0284c7' }}>
                  {Number(selectedCourseForPreview.price) === 0 ? "Free Seminar" : `${formatLKR(Number(selectedCourseForPreview.promoPrice) || Number(selectedCourseForPreview.price))} LKR`}
                </div>
                {Number(selectedCourseForPreview.promoPrice) > 0 && Number(selectedCourseForPreview.promoPrice) < Number(selectedCourseForPreview.price) && (
                  <div style={{ textDecoration: 'line-through', color: '#94a3b8', fontSize: '13px' }}>
                    {formatLKR(Number(selectedCourseForPreview.price))} LKR
                  </div>
                )}
              </div>
            </div>

            {/* Main Video & Content Split */}
            <div className="preview-modal-body" style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 24 }}>
              {/* Left Column: Video Player & Full Unrestricted Description */}
              <div style={{ flex: 1.5, minWidth: 0 }}>
                {/* Active Video Player */}
                <div
                  className="video-viewport"
                  style={{
                    background: '#090d16',
                    borderRadius: 12,
                    overflow: 'hidden',
                    minHeight: 280,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                  }}
                >
                  {activePreviewVideo ? (
                    <iframe
                      width="100%"
                      height="340"
                      src={getEmbedVideoUrl(activePreviewVideo)}
                      title="Course Video Preview"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      style={{ border: 'none' }}
                    />
                  ) : (
                    <div style={{ position: 'relative', width: '100%', height: 280 }}>
                      <img
                        src={selectedCourseForPreview?.coverImage || DEFAULT_COURSE_COVER_IMAGE}
                        alt={selectedCourseForPreview?.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.8 }}
                      />
                      <div
                        style={{
                          position: 'absolute',
                          inset: 0,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: 'rgba(0,0,0,0.45)',
                          color: '#fff',
                          textAlign: 'center',
                          padding: 16,
                        }}
                      >
                        <PlayCircleOutlined style={{ fontSize: 48, marginBottom: 8, color: '#38bdf8' }} />
                        <span style={{ fontWeight: 600, fontSize: '14px' }}>
                          Course Overview Available Below
                        </span>
                        <span style={{ fontSize: '12px', color: '#cbd5e1' }}>
                          Enroll to unlock all video lectures and live sessions
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Full Course Description (UNRESTRICTED LENGTH) */}
                <div style={{ marginTop: 24 }}>
                  <h4 style={{ fontSize: '16px', fontWeight: 800, color: '#03245e', borderBottom: '2px solid #e2e8f0', paddingBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <BookOutlined style={{ color: '#0284c7' }} />
                    Course Overview & Detailed Syllabus
                  </h4>
                  <div
                    className="ql-editor course-full-html-description"
                    style={{ padding: '10px 0', fontSize: '14px', lineHeight: 1.7, color: '#334155' }}
                    dangerouslySetInnerHTML={{
                      __html: selectedCourseForPreview?.description || '<p>Comprehensive hands-on training curriculum.</p>',
                    }}
                  />
                  {selectedCourseForPreview?.description1 && (
                    <div
                      className="ql-editor course-additional-html-description"
                      style={{ padding: '8px 0', fontSize: '14px', lineHeight: 1.7, color: '#334155', marginTop: 8 }}
                      dangerouslySetInnerHTML={{ __html: selectedCourseForPreview.description1 }}
                    />
                  )}
                </div>
              </div>

              {/* Right Column: Free Preview Videos & Course Curriculum */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 16, minWidth: 260 }}>
                {/* Free Preview Lectures Box */}
                <div style={{ background: '#f8fafc', padding: 16, borderRadius: 12, border: '1px solid #e2e8f0' }}>
                  <h4 style={{ fontSize: '14px', fontWeight: 700, color: '#03245e', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <VideoCameraOutlined style={{ color: '#0284c7' }} />
                    Free Preview Lessons ({coursePreviewContents.filter((c) => c.isFree && c.videoUrl).length})
                  </h4>

                  {coursePreviewLoading ? (
                    <div style={{ textAlign: 'center', padding: 20 }}>
                      <Spin tip="Loading curriculum..." />
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 200, overflowY: 'auto' }}>
                      {coursePreviewContents.filter((c) => c.isFree && c.videoUrl).length > 0 ? (
                        coursePreviewContents
                          .filter((c) => c.isFree && c.videoUrl)
                          .map((item, idx) => (
                            <div
                              key={item.id || idx}
                              onClick={() => setActivePreviewVideo(item.videoUrl)}
                              style={{
                                padding: '10px 12px',
                                borderRadius: 8,
                                background: activePreviewVideo === item.videoUrl ? '#e0f2fe' : '#ffffff',
                                border: activePreviewVideo === item.videoUrl ? '1px solid #38bdf8' : '1px solid #e2e8f0',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                transition: 'all 0.2s ease',
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                                <PlayCircleOutlined style={{ color: '#0284c7', fontSize: 16, flexShrink: 0 }} />
                                <span style={{ fontSize: '12px', fontWeight: 600, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {item.title}
                                </span>
                              </div>
                              <Tag color="green" style={{ margin: 0, fontSize: '10px' }}>Free Preview</Tag>
                            </div>
                          ))
                      ) : (
                        <div style={{ padding: '12px', background: '#fff', borderRadius: 8, textAlign: 'center', color: '#94a3b8', fontSize: '12px' }}>
                          {selectedCourseForPreview?.videoUrl
                            ? "Click on the player to watch the introductory overview video."
                            : "Full video lessons unlocked upon student enrollment."}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Course Curriculum Overview */}
                <div style={{ background: '#ffffff', padding: 16, borderRadius: 12, border: '1px solid #e2e8f0' }}>
                  <h4 style={{ fontSize: '14px', fontWeight: 700, color: '#03245e', marginBottom: 10 }}>
                    Curriculum Modules ({coursePreviewContents.length} Lessons)
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 180, overflowY: 'auto' }}>
                    {coursePreviewContents.length > 0 ? (
                      coursePreviewContents.map((content, idx) => (
                        <div
                          key={content.id || idx}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            fontSize: '12px',
                            padding: '6px 0',
                            borderBottom: '1px dashed #f1f5f9',
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                            {content.isFree ? (
                              <UnlockOutlined style={{ color: '#16a34a' }} />
                            ) : (
                              <LockOutlined style={{ color: '#94a3b8' }} />
                            )}
                            <span style={{ color: content.isFree ? '#0f172a' : '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {content.title}
                            </span>
                          </div>
                          {content.isFree ? (
                            <span style={{ color: '#16a34a', fontWeight: 600, fontSize: '11px' }}>Free</span>
                          ) : (
                            <span style={{ color: '#94a3b8', fontSize: '11px' }}>Locked</span>
                          )}
                        </div>
                      ))
                    ) : (
                      <div style={{ color: '#94a3b8', fontSize: '12px', textAlign: 'center', padding: 8 }}>
                        Full module breakdown available upon class commencement.
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 'auto' }}>
                  <Link to="/register" style={{ textDecoration: 'none' }}>
                    <Button
                      type="primary"
                      size="large"
                      block
                      style={{
                        background: '#03245e',
                        borderColor: '#03245e',
                        fontWeight: 700,
                        height: 44,
                        borderRadius: 8,
                      }}
                    >
                      Enroll in This Course →
                    </Button>
                  </Link>

                  {selectedCourseForPreview?.whatsappGroupLink ? (
                    <a href={selectedCourseForPreview.whatsappGroupLink} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
                      <Button
                        block
                        icon={<WhatsAppOutlined />}
                        style={{
                          background: '#25d366',
                          borderColor: '#25d366',
                          color: '#fff',
                          fontWeight: 600,
                          height: 40,
                          borderRadius: 8,
                        }}
                      >
                        Join WhatsApp Group
                      </Button>
                    </a>
                  ) : (
                    <a
                      href={`https://wa.me/94712163163?text=I%20am%20inquiring%20about%20${encodeURIComponent(selectedCourseForPreview?.name)}`}
                      target="_blank"
                      rel="noreferrer"
                      style={{ textDecoration: 'none' }}
                    >
                      <Button
                        block
                        icon={<WhatsAppOutlined />}
                        style={{
                          background: '#25d366',
                          borderColor: '#25d366',
                          color: '#fff',
                          fontWeight: 600,
                          height: 40,
                          borderRadius: 8,
                        }}
                      >
                        Inquire on WhatsApp
                      </Button>
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
      {/* FLOATING MOBILE WHATSAPP SUPPORT FAB */}
      <a
        href="https://wa.me/94712163163?text=Hello%20IBAS,%20I%20would%20like%20to%20inquire%20about%20courses"
        target="_blank"
        rel="noreferrer"
        className="mobile-floating-whatsapp-fab"
        aria-label="Chat with Admissions on WhatsApp"
      >
        <WhatsAppOutlined className="fab-icon" />
        <span className="fab-label">Chat with Us</span>
      </a>
    </div>
  );
};

export default Landing;
